import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  DollarSign,
  User,
  ShoppingCart,
  Trash2,
  FileText,
  Search,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Plus,
  Package,
  Tag,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import quotationsAPI from '.././services/quotationsAPI';
import customersAPI from '../services/customersAPI';
import { useCurrency } from '../contexts/CurrencyContext';

import './QuotationForm.css';
import { getDisplayPhoneNumber } from '../utils/numberMasking';

const QuotationForm = ({
  isOpen,
  onClose,
  onSuccess,
  products = [],
  services = [],
  editQuotation = null,
  leadId = null,
  leadData = null
}) => {
  const { currencySymbol, formatAmount } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [fullQuotationData, setFullQuotationData] = useState(null);

  // -- Subscription Mode State -----------------------------------------------
  const [isSubscriptionMode, setIsSubscriptionMode] = useState(false);
  const [subscriptionConfig, setSubscriptionConfig] = useState({
    startDate: '',
    endDate: '',
    deliveryTimeSlot: 'morning',
    deliveryInstructions: '',
    billingCycle: 'monthly'
  });

  // -- Customer selection state ----------------------------------------------
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);

  // -- Add New Customer Modal State ------------------------------------------
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    // Service Address
    service_address: '',
    service_city: '',
    service_state: '',
    service_pincode: '',
    service_flat_plot_number: '',
    service_floor_lane_no: '',
    service_building_colony_name: '',
    service_society_area_name: '',
    // Billing Address
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_pincode: '',
    billing_flat_plot_number: '',
    billing_floor_lane_no: '',
    billing_building_colony_name: '',
    billing_society_area_name: '',
    // Other fields
    date_of_birth: '',
    gender: '',
    gstin: '',
    pan_number: '',
    legal_name: '',
    trade_name: '',
    gst_registration_type: 'regular',
    gst_state_code: '',
    // Checkbox to copy service to billing
    same_as_service: false
  });
  const [checkingCustomer, setCheckingCustomer] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [isAlreadyLinked, setIsAlreadyLinked] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);

  const customerSearchRef = useRef(null);
  const dropdownWrapperRef = useRef(null);

  // -- Manual Product Entry State --------------------------------------------
  const [showManualProductModal, setShowManualProductModal] = useState(false);
  const [manualProductData, setManualProductData] = useState({
    productName: '',
    quantity: 1,
    price: '',
    unit: '',
    gstRate: '0',
    category: ''
  });
  const [savingProduct, setSavingProduct] = useState(false);

  // -- Collapsible Address Sections State -----------------------------------
  const [collapsedSections, setCollapsedSections] = useState({
    customerInfo: false,
    billingModal: false,
    serviceModal: false
  });

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const [formData, setFormData] = useState({
    customerId: null,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerPincode: '',
    // ? UPDATED: Proper address objects from customer_addresses table
    billingAddress: {
      id: null,
      address: '',
      address_label: '',
      city: '',
      state: '',
      pincode: '',
      society_area_name: '',
      building_colony_name: '',
      flat_plot_number: '',
      floor_lane_no: ''
    },
    shippingAddress: {
      id: null,
      address: '',
      address_label: '',
      city: '',
      state: '',
      pincode: '',
      society_area_name: '',
      building_colony_name: '',
      flat_plot_number: '',
      floor_lane_no: ''
    },
    quotationDate: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    paymentTerms: 'Due within 30 days',
    items: [],
    discountAmount: 0,
    shippingAmount: 0,
    notes: ''
  });

  const [isCustomerPhoneEdited, setIsCustomerPhoneEdited] = useState(false);

  const [activeTab, setActiveTab] = useState('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [allProducts, setAllProducts] = useState(products);
  const [allServices, setAllServices] = useState(services);

  // -- Rerender dropdown on scroll/resize (create mode) ---------------------
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!showCustomerDropdown) return;
    const update = () => forceUpdate(n => n + 1);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showCustomerDropdown]);

  // -- Close dropdown when clicking outside ------------------------------
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownWrapperRef.current && !dropdownWrapperRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // -- Helper function to parse items from database format to frontend format --
  const parseItemsForEdit = (items, isSubscription) => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    console.log('?? Parsing items for edit mode:', { itemCount: items.length, isSubscription });

    return items.map((item, index) => {
      // Start with base item fields
      const parsedItem = {
        item_type: item.item_type || 'product',
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name || '',
        quantity: parseFloat(item.quantity || 1),
        unit_price: parseFloat(item.unit_price || 0),
        product_gst_rate: parseFloat(item.product_gst_rate || item.gst_rate || 0),
        discount_percent: parseFloat(item.discount_percent || 0),
        item_details: item.item_details || null
      };

      // ? If this is a subscription quotation, parse subscription-specific fields
      if (isSubscription) {
        // Parse subscription type (database: subscription_type -> frontend: subscriptionType)
        const subType = item.subscription_type || 'daily';
        parsedItem.subscriptionType = subType;
        
        // Parse repeat cycle (database: repeat_cycle -> frontend: repeatCycleDays)
        parsedItem.repeatCycleDays = parseInt(item.repeat_cycle || 2);
        
        // ? Parse day quantities from database columns (mon, tue, wed, etc.) to frontend object
        const dayQuantities = {
          1: parseFloat(item.mon || 0),  // Monday
          2: parseFloat(item.tue || 0),  // Tuesday
          3: parseFloat(item.wed || 0),  // Wednesday
          4: parseFloat(item.thu || 0),  // Thursday
          5: parseFloat(item.fri || 0),  // Friday
          6: parseFloat(item.sat || 0),  // Saturday
          7: parseFloat(item.sun || 0)   // Sunday
        };

        // ? IMPORTANT: If all day quantities are 0, initialize with the base quantity
        // This ensures items show editable values even if they weren't set before
        const totalDayQty = Object.values(dayQuantities).reduce((sum, qty) => sum + qty, 0);
        if (totalDayQty === 0 && subType === 'daily') {
          // Set default: distribute base quantity across all days
          const baseQty = parseFloat(item.quantity || 1);
          dayQuantities[1] = baseQty;
          dayQuantities[2] = baseQty;
          dayQuantities[3] = baseQty;
          dayQuantities[4] = baseQty;
          dayQuantities[5] = baseQty;
          dayQuantities[6] = baseQty;
          dayQuantities[7] = baseQty;
          console.log(`  ?? No day quantities found for "${parsedItem.item_name}", initializing with base quantity: ${baseQty}`);
        }

        parsedItem.dayQuantities = dayQuantities;

        // Parse delivery days array (which days have deliveries)
        parsedItem.deliveryDays = [];
        for (let day = 1; day <= 7; day++) {
          if (parsedItem.dayQuantities[day] > 0) {
            parsedItem.deliveryDays.push(day);
          }
        }

        // Ensure deliveryDays is not empty for daily subscriptions
        if (subType === 'daily' && parsedItem.deliveryDays.length === 0) {
          parsedItem.deliveryDays = [1, 2, 3, 4, 5, 6, 7]; // All days by default
        }

        // Parse weekly day if applicable
        if (subType === 'weekly') {
          parsedItem.weeklyDay = parseInt(item.weekly_day || 1);
        }

        // ? Add flags for UI controls
        parsedItem.sameQuantityAllDays = false; // Allow independent day editing
        parsedItem.commonQuantity = parseFloat(item.quantity || 1);
        parsedItem.recurringQuantity = parseFloat(item.quantity || 1);

        console.log(`  ? Item ${index + 1} "${parsedItem.item_name}":`, {
          subscriptionType: parsedItem.subscriptionType,
          repeatCycleDays: parsedItem.repeatCycleDays,
          dayQuantities: parsedItem.dayQuantities,
          deliveryDays: parsedItem.deliveryDays,
          totalDayQty: Object.values(parsedItem.dayQuantities).reduce((s, q) => s + q, 0)
        });
      }

      return parsedItem;
    });
  };

  // -- Load quotation for edit mode ------------------------------------------
  useEffect(() => {
    if (editQuotation?.id && isOpen) {
      loadQuotationData(editQuotation.id);
    }
  }, [editQuotation?.id, isOpen]);

  // -- Pre-fill form with lead data when creating from lead ------------------
  useEffect(() => {
    if (leadData && isOpen && !editQuotation) {
      console.log('?? Pre-filling form with lead data:', leadData);
      setFormData(prev => ({
        ...prev,
        customerName: leadData.customer_name || '',
        customerEmail: leadData.email || '',
        customerPhone: leadData.mobile_number || '',
        notes: leadData.notes ? `Lead Notes: ${leadData.notes}` : ''
      }));
      setIsCustomerPhoneEdited(false);
      toast.success(`Creating quotation for: ${leadData.customer_name || getDisplayPhoneNumber(leadData.mobile_number)}`, {
        duration: 3000,
        icon: '??'
      });
    }
  }, [leadData, isOpen, editQuotation]);

  // -- Debounced customer search ---------------------------------------------
  useEffect(() => {
    if (customerSearchQuery.trim().length > 0) {
      searchCustomers(customerSearchQuery);
    } else {
      // Don't clear customers when empty - keep showing defaults if dropdown is open
      if (!showCustomerDropdown) {
        setCustomers([]);
      }
    }
  }, [customerSearchQuery]);

  // -- Load services with images from API ------------------------------------
  const loadServicesWithImages = async () => {
    try {
      console.log('?? Loading services with images...');
      const servicesData = await quotationsAPI.getServicesWithImages(searchQuery);
      console.log(`? Loaded ${servicesData.length} services with images`);
      setAllServices(servicesData);
    } catch (error) {
      console.error('Error loading services with images:', error);
      // Fallback to services passed as props if API fails
      setAllServices(services);
    }
  };

  // -- ? Update useEffect for initial load and search ----------------------
  useEffect(() => {
    if (!isOpen) return;
    
    if (activeTab === 'services') {
      loadServicesWithImages();
    } else {
      setAllProducts(products);
    }
  }, [isOpen, activeTab]);

  // -- ? Update useEffect for search query changes --------------------------
  useEffect(() => {
    if (activeTab === 'services' && (searchQuery.trim().length > 0 || searchQuery === '')) {
      console.log(`?? Searching services for: "${searchQuery}"`);
      loadServicesWithImages();
    }
  }, [searchQuery, activeTab]);

  const searchCustomers = async (query) => {
    try {
      setLoadingCustomers(true);
      // Pass query (can be empty string to get defaults)
      const response = await customersAPI.search(query || '');
      const foundCustomers = response.data?.customers || [];
      setCustomers(foundCustomers);
      // ? Keep dropdown open even when no results - user can still add new customer
      setShowCustomerDropdown(true);
    } catch (error) {
      console.error('Customer search error:', error);
      toast.error('Failed to search customers');
      setCustomers([]);
      // ? Keep dropdown open on error too
      setShowCustomerDropdown(true);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // -- Load default customers when focusing on search ------------------------
  const handleCustomerSearchFocus = async () => {
    // If no customers loaded yet and search is empty, load defaults
    if (customers.length === 0 && !customerSearchQuery.trim()) {
      await searchCustomers('');
    } else if (customers.length > 0) {
      setShowCustomerDropdown(true);
    }
  };

  // -- Select a customer and load their details ------------------------------
  const handleSelectCustomer = async (customer) => {
    try {
      setLoading(true);
      const response = await customersAPI.getById(customer.id);
      const fetchedCustomer = response.data?.customer;
      const addresses = response.data?.addresses || [];

      console.log('?? Customer fetched:', fetchedCustomer);
      console.log('?? Addresses found:', addresses);

      setSelectedCustomer(fetchedCustomer);
      setCustomerAddresses(addresses);
      setShowCustomerDropdown(false);
      setCustomerSearchQuery('');

      // ? Find billing and shipping addresses from customer_addresses table
      const billingAddr = addresses.find(addr => addr.address_type === 'billing' && (addr.is_default || addr.is_primary)) 
                        || addresses.find(addr => addr.address_type === 'billing')
                        || addresses.find(addr => addr.is_default || addr.is_primary);
      
      const shippingAddr = addresses.find(addr => addr.address_type === 'shipping' && (addr.is_default || addr.is_primary))
                         || addresses.find(addr => addr.address_type === 'shipping')
                         || addresses.find(addr => addr.is_default || addr.is_primary)
                         || billingAddr; // Use billing as fallback

      console.log('?? Selected billing address:', billingAddr);
      console.log('?? Selected shipping address:', shippingAddr);

      // ? FIXED: Update all fields in a single setState call to avoid race conditions
      setFormData(prev => ({
        ...prev,
        customerId: fetchedCustomer.id || null,
        customerName: fetchedCustomer.name || '',
        customerEmail: fetchedCustomer.email || '',
        customerPhone: fetchedCustomer.phone || '',
        customerAddress: fetchedCustomer.address || '',
        customerCity: fetchedCustomer.city || '',
        customerState: fetchedCustomer.state || '',
        customerPincode: fetchedCustomer.pincode || '',
        billingAddress: billingAddr ? {
          id: billingAddr.id,
          address: billingAddr.address || '',
          address_label: billingAddr.address_label || 'Billing Address',
          city: billingAddr.city || '',
          state: billingAddr.state || '',
          pincode: billingAddr.pincode || '',
          society_area_name: billingAddr.society_area_name || '',
          building_colony_name: billingAddr.building_colony_name || '',
          flat_plot_number: billingAddr.flat_plot_number || '',
          floor_lane_no: billingAddr.floor_lane_no || ''
        } : prev.billingAddress,
        shippingAddress: shippingAddr ? {
          id: shippingAddr.id,
          address: shippingAddr.address || '',
          address_label: shippingAddr.address_label || 'Shipping Address',
          city: shippingAddr.city || '',
          state: shippingAddr.state || '',
          pincode: shippingAddr.pincode || '',
          society_area_name: shippingAddr.society_area_name || '',
          building_colony_name: shippingAddr.building_colony_name || '',
          flat_plot_number: shippingAddr.flat_plot_number || '',
          floor_lane_no: shippingAddr.floor_lane_no || ''
        } : prev.shippingAddress
      }));

      const addressMsg = billingAddr && shippingAddr 
        ? '? Billing & shipping addresses loaded' 
        : billingAddr 
        ? '? Billing address loaded' 
        : addresses.length > 0
        ? '?? No default address found'
        : '?? No addresses found';
      
      toast.success(`Selected: ${fetchedCustomer.name} � ${addressMsg}`);
    } catch (error) {
      console.error('? Error loading customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectShippingAddress = (address) => {
    setFormData(prev => ({
      ...prev,
      shippingAddress: {
        id: address.id,
        address: address.address || '',
        address_label: address.address_label || 'Shipping Address',
        city: address.city || '',
        state: address.state || '',
        pincode: address.pincode || '',
        society_area_name: address.society_area_name || '',
        building_colony_name: address.building_colony_name || '',
        flat_plot_number: address.flat_plot_number || '',
        floor_lane_no: address.floor_lane_no || ''
      }
    }));
    toast.success('Shipping address selected');
  };

  const handleSelectBillingAddress = (address) => {
    setFormData(prev => ({
      ...prev,
      billingAddress: {
        id: address.id,
        address: address.address || '',
        address_label: address.address_label || 'Billing Address',
        city: address.city || '',
        state: address.state || '',
        pincode: address.pincode || '',
        society_area_name: address.society_area_name || '',
        building_colony_name: address.building_colony_name || '',
        flat_plot_number: address.flat_plot_number || '',
        floor_lane_no: address.floor_lane_no || ''
      }
    }));
    toast.success('Billing address selected');
  };

  // -- Add New Customer Handlers ---------------------------------------------
  const handleOpenAddCustomerModal = () => {
    // Pre-populate with lead data if available
    if (leadData) {
      setNewCustomerData({
        name: leadData.customer_name || '',
        email: leadData.email || '',
        phone: leadData.mobile_number || '',
        service_address: '',
        service_city: '',
        service_state: '',
        service_pincode: '',
        service_flat_plot_number: '',
        service_floor_lane_no: '',
        service_building_colony_name: '',
        service_society_area_name: '',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_pincode: '',
        billing_flat_plot_number: '',
        billing_floor_lane_no: '',
        billing_building_colony_name: '',
        billing_society_area_name: '',
        date_of_birth: '',
        gender: '',
        gstin: '',
        pan_number: '',
        legal_name: '',
        trade_name: '',
        gst_registration_type: 'regular',
        gst_state_code: '',
        same_as_service: false
      });
      toast('?? Lead details pre-filled. Complete the remaining fields.', { duration: 3000 });
    } else {
      setNewCustomerData({
        name: '',
        email: '',
        phone: '',
        service_address: '',
        service_city: '',
        service_state: '',
        service_pincode: '',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_pincode: '',
        date_of_birth: '',
        gender: '',
        gstin: '',
        pan_number: '',
        legal_name: '',
        trade_name: '',
        gst_registration_type: 'regular',
        gst_state_code: '',
        same_as_service: false
      });
    }
    setExistingCustomer(null);
    setShowAddCustomerModal(true);
  };

  const handleCloseAddCustomerModal = () => {
    setShowAddCustomerModal(false);
    setExistingCustomer(null);
    setIsAlreadyLinked(false);
    setNewCustomerData({
      name: '',
      email: '',
      phone: '',
      service_address: '',
      service_city: '',
      service_state: '',
      service_pincode: '',
      billing_address: '',
      billing_city: '',
      billing_state: '',
      billing_pincode: '',
      date_of_birth: '',
      gender: '',
      gstin: '',
      pan_number: '',
      legal_name: '',
      trade_name: '',
      gst_registration_type: 'regular',
      gst_state_code: '',
      same_as_service: false
    });
  };

  const handleNewCustomerChange = (field, value) => {
    setNewCustomerData(prev => ({ ...prev, [field]: value }));
  };

  // -- Handler for "Same as Billing Address" checkbox ------------------------
  const handleSameAsBillingChange = (checked) => {
    if (checked) {
      // Copy billing address to service address
      setNewCustomerData(prev => ({
        ...prev,
        same_as_service: true,
        service_address: prev.billing_address,
        service_city: prev.billing_city,
        service_state: prev.billing_state,
        service_pincode: prev.billing_pincode,
        service_flat_plot_number: prev.billing_flat_plot_number,
        service_floor_lane_no: prev.billing_floor_lane_no,
        service_building_colony_name: prev.billing_building_colony_name,
        service_society_area_name: prev.billing_society_area_name
      }));
    } else {
      // Uncheck and clear service address
      setNewCustomerData(prev => ({
        ...prev,
        same_as_service: false,
        service_address: '',
        service_city: '',
        service_state: '',
        service_pincode: '',
        service_flat_plot_number: '',
        service_floor_lane_no: '',
        service_building_colony_name: '',
        service_society_area_name: ''
      }));
    }
  };

  // -- Auto-fetch customer data when email field loses focus ----------------
  const handleEmailBlur = async () => {
    const email = newCustomerData.email?.trim();
    const phone = newCustomerData.phone?.trim();

    // Need at least email or phone to check
    if (!email && !phone) {
      setExistingCustomer(null);
      setIsAlreadyLinked(false);
      return;
    }

    try {
      setCheckingCustomer(true);
      const response = await customersAPI.checkExists(email, phone);
      
      if (response.exists && response.customer) {
        const customer = response.customer;
        const alreadyLinked = response.isAlreadyLinked || false;
        
        setExistingCustomer(customer);
        setIsAlreadyLinked(alreadyLinked);
        
        // Get billing and service addresses from the customer object
        const billingAddr = customer.billing_address || {};
        const serviceAddr = customer.service_address || {};
        
        // Auto-populate form with existing customer data
        setNewCustomerData({
          name: customer.name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          // Service address with detailed fields
          service_address: serviceAddr.address || customer.address || '',
          service_city: serviceAddr.city || customer.city || '',
          service_state: serviceAddr.state || customer.state || '',
          service_pincode: serviceAddr.pincode || customer.pincode || '',
          service_flat_plot_number: serviceAddr.flat_plot_number || '',
          service_floor_lane_no: serviceAddr.floor_lane_no || '',
          service_building_colony_name: serviceAddr.building_colony_name || '',
          service_society_area_name: serviceAddr.society_area_name || '',
          // Billing address with detailed fields
          billing_address: billingAddr.address || customer.address || '',
          billing_city: billingAddr.city || customer.city || '',
          billing_state: billingAddr.state || customer.state || '',
          billing_pincode: billingAddr.pincode || customer.pincode || '',
          billing_flat_plot_number: billingAddr.flat_plot_number || '',
          billing_floor_lane_no: billingAddr.floor_lane_no || '',
          billing_building_colony_name: billingAddr.building_colony_name || '',
          billing_society_area_name: billingAddr.society_area_name || '',
          // Other customer fields
          date_of_birth: customer.date_of_birth || '',
          gender: customer.gender || '',
          gstin: customer.gstin || '',
          pan_number: customer.pan_number || '',
          legal_name: customer.legal_name || '',
          trade_name: customer.trade_name || '',
          gst_registration_type: customer.gst_registration_type || 'regular',
          gst_state_code: customer.gst_state_code || '',
          same_as_service: false
        });
        
        if (alreadyLinked) {
          toast.info(`?? Customer "${customer.name}" is already linked to your account`);
        } else {
          toast.success(`? Found existing customer: ${customer.name}`);
        }
      } else {
        setExistingCustomer(null);
        setIsAlreadyLinked(false);
      }
    } catch (error) {
      console.error('Error checking customer:', error);
      // Don't show error toast, just silently fail
      setExistingCustomer(null);
      setIsAlreadyLinked(false);
    } finally {
      setCheckingCustomer(false);
    }
  };

  const handleSaveNewCustomer = async () => {
    // Validation
    if (!newCustomerData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!newCustomerData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }

    // Prevent re-linking if customer is already linked
    if (isAlreadyLinked && existingCustomer) {
      toast.error(`? Customer "${existingCustomer.name}" is already linked to your account. Please select them from the dropdown instead.`);
      return;
    }

    try {
      setSavingCustomer(true);
      
      // Use the createOrLink API endpoint
      const response = await customersAPI.createOrLink(newCustomerData);
      
      if (response.success) {
        const customer = response.data.customer;
        const isNewCustomer = response.data.isNewCustomer;

        // Populate form with customer data
        setFormData(prev => ({
          ...prev,
          customerId: customer.id || null,
          customerName: customer.name || '',
          customerEmail: customer.email || '',
          customerPhone: customer.phone || '',
          customerAddress: customer.address || '',
          customerCity: customer.city || '',
          customerState: customer.state || '',
          customerPincode: customer.pincode || ''
        }));

        setSelectedCustomer(customer);
        
        if (isNewCustomer) {
          toast.success('? New customer created and linked!');
        } else {
          toast.success('? Existing customer linked to your account!');
        }
        
        handleCloseAddCustomerModal();
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      const errorMessage = error?.error || error?.message || 'Failed to save customer';
      toast.error(errorMessage);
    } finally {
      setSavingCustomer(false);
    }
  };

  // -- Load full quotation data for edit -------------------------------------
  const loadQuotationData = async (quotationId) => {
    try {
      setLoading(true);
      const response = await quotationsAPI.getById(quotationId);
      const quotation = response?.data?.quotation;
      if (!quotation) {
        toast.error('Failed to load quotation data');
        return;
      }
      setFullQuotationData(quotation);
      
      // ? Check if this is a subscription quotation and restore subscription mode
      if (quotation.is_subscription) {
        console.log('?? Loading SUBSCRIPTION quotation for edit');
        setIsSubscriptionMode(true);
        
        // Parse subscription config (handle both JSON string and object)
        let parsedConfig = null;
        if (typeof quotation.subscription_config === 'string') {
          try {
            parsedConfig = JSON.parse(quotation.subscription_config);
          } catch (e) {
            console.error('Failed to parse subscription_config:', e);
          }
        } else if (quotation.subscription_config) {
          parsedConfig = quotation.subscription_config;
        }
        
        // Set subscription configuration
        if (parsedConfig) {
          setSubscriptionConfig({
            startDate: parsedConfig.start_date || parsedConfig.startDate || '',
            endDate: parsedConfig.end_date || parsedConfig.endDate || '',
            deliveryTimeSlot: parsedConfig.delivery_time_slot || parsedConfig.deliveryTimeSlot || 'morning',
            deliveryInstructions: parsedConfig.delivery_instructions || parsedConfig.deliveryInstructions || '',
            billingCycle: parsedConfig.billing_cycle || parsedConfig.billingCycle || 'monthly'
          });
          console.log('? Restored subscription config:', parsedConfig);
        }
      } else {
        console.log('?? Loading NORMAL quotation for edit');
        setIsSubscriptionMode(false);
      }
      
      // ? Parse address strings into address objects for edit mode
      const parseBillingAddress = (addressString) => {
        if (!addressString) {
          return {
            id: null,
            address: '',
            address_label: 'Billing Address',
            city: '',
            state: '',
            pincode: '',
            society_area_name: '',
            building_colony_name: '',
            flat_plot_number: '',
            floor_lane_no: ''
          };
        }
        return {
          id: null,
          address: addressString,
          address_label: 'Billing Address',
          city: quotation.customer_city || '',
          state: quotation.customer_state || '',
          pincode: quotation.customer_pincode || '',
          society_area_name: '',
          building_colony_name: '',
          flat_plot_number: '',
          floor_lane_no: ''
        };
      };

      const parseShippingAddress = (addressString) => {
        if (!addressString) {
          return {
            id: null,
            address: '',
            address_label: 'Shipping Address',
            city: '',
            state: '',
            pincode: '',
            society_area_name: '',
            building_colony_name: '',
            flat_plot_number: '',
            floor_lane_no: ''
          };
        }
        return {
          id: null,
          address: addressString,
          address_label: 'Shipping Address',
          city: quotation.customer_city || '',
          state: quotation.customer_state || '',
          pincode: quotation.customer_pincode || '',
          society_area_name: '',
          building_colony_name: '',
          flat_plot_number: '',
          floor_lane_no: ''
        };
      };

      setFormData({
        customerId: quotation.customer_id || null,
        customerName: quotation.customer_name || '',
        customerEmail: quotation.customer_email || '',
        customerPhone: quotation.customer_phone || '',
        customerAddress: quotation.customer_address || '',
        customerCity: quotation.customer_city || '',
        customerState: quotation.customer_state || '',
        customerPincode: quotation.customer_pincode || '',
        billingAddress: parseBillingAddress(quotation.billing_address),
        shippingAddress: parseShippingAddress(quotation.shipping_address),
        quotationDate: quotation.quotation_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        validUntil: quotation.valid_until?.split('T')[0] || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        paymentTerms: quotation.payment_terms || 'Due within 30 days',
        items: parseItemsForEdit(quotation.items || [], quotation.is_subscription), // ? FIXED: Parse items with subscription fields
        discountAmount: parseFloat(quotation.discount_amount || 0),
        shippingAmount: parseFloat(quotation.shipping_amount || 0),
        notes: quotation.notes || ''
      });

      // ? Mark items as selected in the catalog
      if (quotation.items && quotation.items.length > 0) {
        const itemIds = quotation.items
          .map(item => item.item_id || item.id)
          .filter(id => id); // Remove any null/undefined IDs
        setSelectedItemIds(new Set(itemIds));
        console.log(`? Marked ${itemIds.length} items as selected in catalog`);
      }
    } catch (error) {
      console.error('Error loading quotation:', error);
      toast.error('Failed to load quotation: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // -- Handle Manual Product Entry - NOW SAVES TO DATABASE --------------------
  const handleAddManualProduct = async (e) => {
    e.preventDefault();

    // ? Validate 5 fields
    if (!manualProductData.productName || !manualProductData.productName.trim()) {
      toast.error('Product name is required');
      return;
    }

    if (!manualProductData.price || parseFloat(manualProductData.price) <= 0) {
      toast.error('Valid price is required');
      return;
    }

    if (!manualProductData.unit || !manualProductData.unit.trim()) {
      toast.error('Unit is required');
      return;
    }

    if (!manualProductData.category || !manualProductData.category.trim()) {
      toast.error('Category is required');
      return;
    }

    if (manualProductData.gstRate === undefined || manualProductData.gstRate === null || parseFloat(manualProductData.gstRate) < 0) {
      toast.error('Valid GST rate is required');
      return;
    }

    try {
      setSavingProduct(true);

      // ? Send 5 fields to save to database
      const productResponse = await quotationsAPI.saveManualProduct({
        product_name: manualProductData.productName.trim(),
        price: parseFloat(manualProductData.price),  // ? Will be stored as MRP
        unit: manualProductData.unit.trim(),
        gst_rate: parseFloat(manualProductData.gstRate) || 0,
        category: manualProductData.category.trim()
      });

      if (!productResponse.success || !productResponse.data?.product?.id) {
        throw new Error('Failed to save product');
      }

      const savedProduct = productResponse.data.product;
      const productNameToShow = manualProductData.productName.trim();

      console.log('?? Saved product from database:', savedProduct);

      // ? INSTANTLY ADD TO PRODUCT CATALOG
      // This makes the product immediately visible in the product selection dropdown
      setAllProducts(prev => {
        // Check if product already exists
        const exists = prev.some(p => p.id === savedProduct.id);
        
        if (exists) {
          return prev;
        }

        // Add new product at the top of catalog
        return [{
          id: savedProduct.id,
          product_name: savedProduct.product_name,
          name: savedProduct.product_name,
          mrp: savedProduct.mrp,
          price: savedProduct.mrp,  // Use MRP as price
          unit: savedProduct.unit,
          gst_rate: savedProduct.gst_rate,
          category: savedProduct.category,
          is_active: true,
          available_for_sale: true,
          isManualProduct: true  // ? Flag for manual product
        }, ...prev];
      });

      console.log(`? Product added to catalog instantly`);

      // ? ALSO ADD TO QUOTATION ITEMS
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_type: 'product',
          item_id: savedProduct.id,
          item_name: productNameToShow,
          quantity: 1,
          unit_price: parseFloat(manualProductData.price),
          product_gst_rate: manualProductData.gstRate !== undefined && manualProductData.gstRate !== null && manualProductData.gstRate !== '' ? parseFloat(manualProductData.gstRate) : 0,
          discount_percent: 0,
          item_details: savedProduct,
          isManualProduct: true  // ? Flag for manual product
        }]
      }));

      // ? Mark as selected in catalog
      setSelectedItemIds(prev => new Set([...prev, savedProduct.id]));

      // ? Reset form
      setManualProductData({
        productName: '',
        price: '',
        unit: '',
        gstRate: '0',
        category: ''
      });

      setShowManualProductModal(false);

      toast.success(`? ${productNameToShow} saved to catalog and added to quotation!`);

      console.log('? Manual product workflow completed successfully');

    } catch (error) {
      console.error('Error adding manual product:', error);
      toast.error('Failed to add product: ' + (error.message || 'Unknown error'));
    } finally {
      setSavingProduct(false);
    }
  };

  // -- Helper function to calculate delivery count for subscription items ------
  const calculateSubscriptionDeliveries = (item, startDate, endDate) => {
    if (!isSubscriptionMode || !startDate || !endDate) {
      return 1; // Default to 1 if not subscription mode
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    if (totalDays <= 0) return 0;

    const subscriptionType = item.subscriptionType || 'daily';

    if (subscriptionType === 'daily') {
      // For daily subscription, sum up quantities based on day-wise quantities
      const dayQuantities = item.dayQuantities || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
      
      // Calculate total quantity delivered across the entire period
      let totalQuantityDelivered = 0;
      for (let i = 0; i < totalDays; i++) {
        const currentDate = new Date(start);
        currentDate.setDate(start.getDate() + i);
        const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Convert Sunday (0) to 7
        const dayQty = parseFloat(dayQuantities[dayOfWeek] || 0);
        totalQuantityDelivered += dayQty;
      }
      
      console.log(`?? Daily subscription for "${item.item_name}": ${totalQuantityDelivered} total units over ${totalDays} days`);
      return totalQuantityDelivered;
      
    } else if (subscriptionType === 'alternate_days') {
      // For alternate days, count the number of delivery occurrences
      const repeatCycle = parseInt(item.repeatCycleDays || 2);
      // If repeatCycle is 2, we deliver on day 1, 3, 5, 7, etc.
      // Number of deliveries = Math.ceil(totalDays / repeatCycle)
      const deliveryCount = Math.ceil(totalDays / repeatCycle);
      console.log(`?? Alternate days subscription for "${item.item_name}": ${deliveryCount} deliveries (every ${repeatCycle} days)`);
      return deliveryCount;
      
    } else if (subscriptionType === 'weekly') {
      // For weekly, count the number of weeks
      const weeks = Math.ceil(totalDays / 7);
      console.log(`?? Weekly subscription for "${item.item_name}": ${weeks} deliveries`);
      return weeks;
      
    } else if (subscriptionType === 'monthly') {
      // For monthly, count the number of months (approximately)
      const months = Math.ceil(totalDays / 30);
      console.log(`?? Monthly subscription for "${item.item_name}": ${months} deliveries`);
      return months;
    }

    return 1;
  };

  // -- Helper function to calculate effective quantity for subscription items ------
  const getEffectiveQuantity = (item) => {
    if (!isSubscriptionMode) {
      return parseFloat(item.quantity || 0);
    }
    
    const baseQuantity = parseFloat(item.quantity || 0);
   const deliveryCount = calculateSubscriptionDeliveries(item, subscriptionConfig.startDate, subscriptionConfig.endDate);
    
    // ? FIXED: For daily subscription, deliveryCount already includes day-wise quantities summed up
    // So we return it directly WITHOUT multiplying by baseQuantity
    if (item.subscriptionType === 'daily') {
      return deliveryCount; // Already the total quantity across all days
    }
    
    // For other types (alternate_days, weekly, monthly), multiply base quantity by delivery count
    return baseQuantity * deliveryCount;
  };

  // -- Calculate Subtotal WITHOUT GST (base amounts only, after discount) ------
  const subtotal = formData.items.reduce((sum, item) => {
    if (!item.item_name || item.item_name.trim() === '') return sum;
    
    const effectiveQuantity = getEffectiveQuantity(item);
    const itemGross = effectiveQuantity * parseFloat(item.unit_price || 0);
    const itemDiscount = (itemGross * parseFloat(item.discount_percent || 0)) / 100;
    const itemTaxable = itemGross - itemDiscount;
    return sum + itemTaxable; // Subtotal WITHOUT GST
  }, 0);

  // -- Calculate GST from items (with discount applied first) ---------------------
  const calculateGST = () => {
    let totalGST = 0;
    let gstRateBreakdown = {};

    formData.items.forEach(item => {
      if (!item.item_name || item.item_name.trim() === '') return;
      
      const quantity = getEffectiveQuantity(item);
      const unitPrice = parseFloat(item.unit_price || 0);
      const itemGross = quantity * unitPrice;
      const discountPercent = parseFloat(item.discount_percent || 0);
      const itemDiscount = (itemGross * discountPercent) / 100;
      const itemTaxable = itemGross - itemDiscount;
      // Fix: Properly handle 0% GST rate
      const gstRate = item.product_gst_rate !== undefined && item.product_gst_rate !== null
        ? parseFloat(item.product_gst_rate)
        : (item.gst_rate !== undefined && item.gst_rate !== null ? parseFloat(item.gst_rate) : 0);
      const itemGST = (itemTaxable * gstRate) / 100;

      totalGST += itemGST;

      if (!gstRateBreakdown[gstRate]) {
        gstRateBreakdown[gstRate] = 0;
      }
      gstRateBreakdown[gstRate] += itemGST;
    });

    return {
      totalGST,
      breakdown: gstRateBreakdown,
      dominantRate: Object.keys(gstRateBreakdown).length > 0
        ? Math.max(...Object.keys(gstRateBreakdown).map(Number))
        : 0
    };
  };

  const gstCalculation = calculateGST();
  const gstAmount = gstCalculation.totalGST;
  
  // Calculate total item discount (for display purposes)
  const itemDiscountAmount = formData.items.reduce((sum, item) => {
    if (!item.item_name || item.item_name.trim() === '') return sum;
    const effectiveQty = getEffectiveQuantity(item);
    const itemGross = effectiveQty * parseFloat(item.unit_price || 0);
    return sum + ((itemGross * parseFloat(item.discount_percent || 0)) / 100);
  }, 0);
  
  const discountAmount = parseFloat(formData.discountAmount) || 0;
  const shippingAmount = parseFloat(formData.shippingAmount) || 0;
  const totalAmount = subtotal + gstAmount + shippingAmount - discountAmount;

  const handleInputChange = (field, value) => {
    if (field === 'customerPhone') {
      setIsCustomerPhoneEdited(true);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCardClick = (item, category) => {
    const itemName = item.name || item.productName || item.product_name || '';
    const itemPrice = parseFloat(item.mrp || item.price || 0);
    // Fix: Use nullish coalescing to properly handle 0% GST
    const itemGstRate = item.gst_rate !== undefined && item.gst_rate !== null ? parseFloat(item.gst_rate) : 0;
    const itemKey = `${category}-${item.id}`;

    const existingIndex = formData.items.findIndex(i => i.item_id === item.id);
    
    // If item exists, remove it (toggle off)
    if (existingIndex !== -1) {
      const newSelected = new Set(selectedItemIds);
      newSelected.delete(itemKey);
      setSelectedItemIds(newSelected);
      
      const newItems = formData.items.filter((_, index) => index !== existingIndex);
      setFormData(prev => ({ ...prev, items: newItems }));
      toast.success(`${itemName} removed from quotation`);
      return;
    }
    
    // If item doesn't exist, add it (toggle on)
    const newSelected = new Set(selectedItemIds);
    newSelected.add(itemKey);
    setSelectedItemIds(newSelected);
    const newItem = {
      item_type: category === 'products' ? 'product' : 'service',
      item_id: item.id,
      item_name: itemName,
      quantity: 1,
      unit_price: itemPrice,
      product_gst_rate: itemGstRate,
      discount_percent: 0,
      item_details: item
    };

      // Add subscription-specific fields if in subscription mode
      if (isSubscriptionMode) {
        newItem.subscriptionType = 'daily';
        newItem.repeatCycleDays = 2;
        newItem.weeklyDay = 1;
        newItem.recurringQuantity = 1;
        newItem.deliveryDays = [1, 2, 3, 4, 5, 6, 7];
        newItem.sameQuantityAllDays = true;
        newItem.commonQuantity = 1;
        newItem.dayQuantities = { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1, 7: 1 };
      }

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    toast.success(`${itemName} added to quotation`);
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  // -- Helper function to format address object to string --------------------
  const formatAddressToString = (addressObj) => {
    if (!addressObj) return '';
    
    const parts = [];
    
    if (addressObj.flat_plot_number) parts.push(addressObj.flat_plot_number);
    if (addressObj.floor_lane_no) parts.push(addressObj.floor_lane_no);
    if (addressObj.building_colony_name) parts.push(addressObj.building_colony_name);
    if (addressObj.society_area_name) parts.push(addressObj.society_area_name);
    if (addressObj.address) parts.push(addressObj.address);
    
    const line1 = parts.join(', ');
    
    const cityStateParts = [];
    if (addressObj.city) cityStateParts.push(addressObj.city);
    if (addressObj.state) cityStateParts.push(addressObj.state);
    if (addressObj.pincode) cityStateParts.push(addressObj.pincode);
    
    const line2 = cityStateParts.join(', ');
    
    return [line1, line2].filter(Boolean).join('\n');
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  const filteredProducts = allProducts.filter(p => {
    const name = p.name || p.productName || p.product_name || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredServices = allServices.filter(s => {
    const name = s.name || s.serviceName || s.service_name || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.customerPhone) {
      toast.error('Customer name and phone are required');
      return;
    }
    const validItems = formData.items.filter(item => item.item_name && item.item_name.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (! formData.validUntil) {
      toast.error('Valid until date is required');
      return;
    }

    // Additional validation for subscription mode
    if (isSubscriptionMode) {
      if (!subscriptionConfig.startDate) {
        toast.error('Start date is required for subscription quotation');
        return;
      }
      if (!subscriptionConfig.endDate) {
        toast.error('End date is required for subscription quotation');
        return;
      }
      // Validate each subscription item has required fields
      for (const item of validItems) {
        if (item.subscriptionType === 'daily' && (!item.deliveryDays || item.deliveryDays.length === 0)) {
          toast.error(`Please select at least one delivery day for ${item.item_name}`);
          return;
        }
      }
    }

    setLoading(true);
    try {
      const gstData = calculateGST();
      const payloadItems = validItems.map(item => {
        const details = item.item_details || {};
        const category = details.category || item.category || null;
        const payload = {
          item_type: item.item_type || 'product',
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          product_gst_rate: item.product_gst_rate ?? item.gst_rate ?? 0,
          discount_percent: item.discount_percent || 0,
          item_description: item.item_description || null,
          category
        };

        if (category) {
          payload.item_details = { category };
        }

        if (isSubscriptionMode) {
          payload.subscriptionType = item.subscriptionType;
          payload.repeatCycleDays = item.repeatCycleDays;
          payload.dayQuantities = item.dayQuantities;
          payload.weeklyDay = item.weeklyDay;
          payload.commonQuantity = item.commonQuantity;
          payload.recurringQuantity = item.recurringQuantity;
        }

        return payload;
      });

      // ? Format address objects to strings for backend
      const formattedBillingAddress = formatAddressToString(formData.billingAddress);
      const formattedShippingAddress = formatAddressToString(formData.shippingAddress);

      const submitData = {
        customerId: formData.customerId,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        customerAddress: formData.customerAddress,
        customerCity: formData.customerCity,
        customerState: formData.customerState,
        customerPincode: formData.customerPincode,
        shippingAddress: formattedShippingAddress,
        billingAddress: formattedBillingAddress,
        quotationDate: formData.quotationDate,
        validUntil: formData.validUntil,
        paymentTerms: formData.paymentTerms,
        items: payloadItems,
        subtotal,
        taxAmount: gstData.totalGST,
        discountAmount,
        shippingAmount: formData.shippingAmount,
        totalAmount,
        notes: formData.notes,
        leadId: leadId,
        // Subscription-specific fields
        isSubscription: isSubscriptionMode,
        subscriptionConfig: isSubscriptionMode ? subscriptionConfig : null,
        serviceType: isSubscriptionMode ? 'daily' : null // Default to daily for subscription
      };

      let result;
      if (editQuotation?.id) {
        result = await quotationsAPI.update(editQuotation.id, submitData);
        toast.success(`? New version created: ${result.data?.quotation?.quotation_number}`);
      } else {
        result = await quotationsAPI.create(submitData);
        const quotationType = isSubscriptionMode ? 'Subscription quotation' : 'Quotation';
        toast.success(`? ${quotationType} created! You can now send it via email from the actions menu.`, { duration: 5000 });
      }

      if (onSuccess) onSuccess(result.data || result);
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || error.data?.message || 'Failed to save quotation');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // ==========================================================================
  // EDIT MODE
  // ==========================================================================
  if (editQuotation?.id) {
    return (
      <div className="quotation-modal-overlay" onClick={onClose}>
        <div className="quotation-modal quotation-modal-edit" onClick={(e) => e.stopPropagation()}>

          <div className="quotation-modal-header">
            <div>
              <h2>Edit Quotation</h2>
              {loading && <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.7 }}>Loading data...</p>}
            </div>
            <button onClick={onClose} className="quotation-close-btn"><X size={24} /></button>
          </div>

          <div className="quotation-modal-content quotation-edit-content">
            {loading ? (
              <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading quotation details...</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px', paddingBottom: '16px' }}>

                {/* -- Customer Information -- */}
                <div className="edit-section">
                 <h3 className="edit-section-heading">Customer Information</h3>
                  <div className="edit-form-grid">
                    <div className="edit-form-group">
                      <label>Customer Name *</label>
                      <input type="text" value={formData.customerName} onChange={(e) => handleInputChange('customerName', e.target.value)} placeholder="Enter customer name" required />
                    </div>
                    <div className="edit-form-group">
                      <label>Phone *</label>
                      <input
                        type="tel"
                        value={formData.customerPhone}
                        onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                        placeholder="10-digit number"
                        required
                      />
                    </div>
                    <div className="edit-form-group">
                      <label>Email</label>
                      <input type="email" value={formData.customerEmail} onChange={(e) => handleInputChange('customerEmail', e.target.value)} placeholder="customer@email.com" />
                    </div>
                    <div className="edit-form-group">
                      <label>City</label>
                      <input type="text" value={formData.customerCity} onChange={(e) => handleInputChange('customerCity', e.target.value)} placeholder="City" />
                    </div>
                    <div className="edit-form-group">
                      <label>State</label>
                      <input type="text" value={formData.customerState} onChange={(e) => handleInputChange('customerState', e.target.value)} placeholder="State" />
                    </div>
                    <div className="edit-form-group">
                      <label>Pincode</label>
                      <input type="text" value={formData.customerPincode} onChange={(e) => handleInputChange('customerPincode', e.target.value)} placeholder="Pincode" />
                    </div>
                    <div className="edit-form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Billing Address</label>
                      <textarea 
                        value={typeof formData.billingAddress === 'object' ? formData.billingAddress.address || '' : formData.billingAddress || ''} 
                        onChange={(e) => {
                          if (typeof formData.billingAddress === 'object') {
                            handleInputChange('billingAddress', { ...formData.billingAddress, address: e.target.value });
                          } else {
                            handleInputChange('billingAddress', e.target.value);
                          }
                        }} 
                        placeholder="Full Billing Address" 
                        rows="2" 
                      />
                    </div>
                    <div className="edit-form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Shipping Address</label>
                      <textarea 
                        value={typeof formData.shippingAddress === 'object' ? formData.shippingAddress.address || '' : formData.shippingAddress || ''} 
                        onChange={(e) => {
                          if (typeof formData.shippingAddress === 'object') {
                            handleInputChange('shippingAddress', { ...formData.shippingAddress, address: e.target.value });
                          } else {
                            handleInputChange('shippingAddress', e.target.value);
                          }
                        }} 
                        placeholder="Enter shipping/delivery address" 
                        rows="2" 
                      />
                    </div>
                  </div>
                </div>

                {/* -- Items -- */}
                <div className="edit-section">
                  <h3 className="edit-section-heading">Selected Items</h3>
                  {formData.items.length === 0 ? (
                    <div className="empty-message">No items added. Add items to quotation.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {formData.items.map((item, index) => (
                        <div key={index} style={{ backgroundColor: 'white', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '20px', position: 'relative', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            style={{ position: 'absolute', top: '16px', right: '16px', width: '32px', height: '32px', borderRadius: '6px', background: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fecaca'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fee2e2'}
                          >
                            <Trash2 size={14} />
                          </button>
                          
                          {/* Item Header */}
                          <div style={{ marginBottom: '16px', paddingRight: '50px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#6366f1' }}>Item {index + 1}</h4>
                            {item.item_details?.category && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '700', color: '#6366f1', background: '#ede9fe', padding: '4px 10px', borderRadius: '20px' }}>
                                <Tag size={11} />
                                {item.item_details.category}
                              </span>
                            )}
                          </div>
                          
                          {/* ROW 1: Input Fields */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', marginBottom: '14px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Item Name *</label>
                              <input 
                                type="text" 
                                value={item.item_name} 
                                onChange={(e) => handleItemChange(index, 'item_name', e.target.value)} 
                                placeholder="Item name" 
                                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Quantity *</label>
                              <input 
                                type="number" 
                                min="1" 
                                value={item.quantity} 
                                onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 1)} 
                                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Unit Price *</label>
                              <input 
                                type="number" 
                                min="0" 
                                step="0.01" 
                                value={item.unit_price} 
                                onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)} 
                                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Discount %</label>
                              <input 
                                type="number" 
                                min="0" 
                                max="100" 
                                step="0.01" 
                                value={item.discount_percent || 0} 
                                onChange={(e) => handleItemChange(index, 'discount_percent', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))} 
                                placeholder="0" 
                                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>GST Rate %</label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={item.product_gst_rate !== undefined && item.product_gst_rate !== null ? item.product_gst_rate : 0}
                                onChange={(e) => handleItemChange(index, 'product_gst_rate', e.target.value !== '' ? parseFloat(e.target.value) : 0)}
                                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', backgroundColor: '#f9fafb', width: '100%', boxSizing: 'border-box' }}
                              />
                            </div>
                          </div>
                          
                          {/* ROW 2: Calculated Values - GST first, Total last */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', paddingTop: '4px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>GST Amount</label>
                              <div style={{ padding: '8px 10px', backgroundColor: '#fef3c7', border: '2px solid #fcd34d', borderRadius: '6px', fontSize: '13px', fontWeight: '800', color: '#92400e', textAlign: 'center', boxSizing: 'border-box' }}>
                                {currencySymbol}{(() => {
                                  const effectiveQty = getEffectiveQuantity(item);
                                  const itemGross = effectiveQty * parseFloat(item.unit_price || 0);
                                  const itemDiscount = (itemGross * parseFloat(item.discount_percent || 0)) / 100;
                                  const itemTaxable = itemGross - itemDiscount;
                                  const gstRate = item.product_gst_rate !== undefined && item.product_gst_rate !== null ? parseFloat(item.product_gst_rate) : 0;
                                  return ((itemTaxable * gstRate) / 100).toFixed(2);
                                })()}
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Discount Amount</label>
                              <div style={{ padding: '8px 10px', backgroundColor: '#dcfce7', border: '2px solid #86efac', borderRadius: '6px', fontSize: '13px', fontWeight: '800', color: '#166534', textAlign: 'center', boxSizing: 'border-box' }}>
                                {currencySymbol}{(() => {
                                  const effectiveQty = getEffectiveQuantity(item);
                                  return ((effectiveQty * parseFloat(item.unit_price || 0) * parseFloat(item.discount_percent || 0)) / 100).toFixed(2);
                                })()}
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Amount</label>
                              <div style={{ padding: '8px 10px', backgroundColor: '#e0e7ff', border: '2px solid #c7d2fe', borderRadius: '6px', fontSize: '13px', fontWeight: '800', color: '#4f46e5', textAlign: 'center', boxSizing: 'border-box' }}>
                                {currencySymbol}{(() => {
                                  const effectiveQty = getEffectiveQuantity(item);
                                  const itemGross = effectiveQty * parseFloat(item.unit_price || 0);
                                  const itemDiscount = (itemGross * parseFloat(item.discount_percent || 0)) / 100;
                                  const itemTaxable = itemGross - itemDiscount;
                                  const gstRate = item.product_gst_rate !== undefined && item.product_gst_rate !== null ? parseFloat(item.product_gst_rate) : 0;
                                  const itemTax = (itemTaxable * gstRate) / 100;
                                  return (itemTaxable + itemTax).toFixed(2);
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* ? SUBSCRIPTION CONTROLS IN EDIT MODE */}
                          {isSubscriptionMode && (
                            <>
                              {/* Subscription Type Selector */}
                              <div style={{ marginTop: '16px', padding: '14px', backgroundColor: '#f0fdfa', border: '2px solid #5eead4', borderRadius: '8px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: item.subscriptionType === 'alternate_days' ? '2fr 1fr' : '1fr', gap: '12px' }}>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#115e59', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                      Subscription Type
                                    </label>
                                    <select
                                      value={item.subscriptionType || 'daily'}
                                      onChange={(e) => {
                                        const updatedItems = [...formData.items];
                                        updatedItems[index].subscriptionType = e.target.value;
                                        // Initialize day quantities for daily type
                                        if (e.target.value === 'daily' && !updatedItems[index].dayQuantities) {
                                          updatedItems[index].dayQuantities = { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1, 7: 1 };
                                        }
                                        // Initialize repeat cycle for alternate days
                                        if (e.target.value === 'alternate_days' && !updatedItems[index].repeatCycleDays) {
                                          updatedItems[index].repeatCycleDays = 2;
                                        }
                                        setFormData(prev => ({ ...prev, items: updatedItems }));
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '2px solid #5eead4',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#115e59',
                                        backgroundColor: 'white'
                                      }}
                                    >
                                      <option value="daily">Daily (with day-wise quantities)</option>
                                      <option value="alternate_days">Alternate Days</option>
                                      <option value="weekly">Weekly</option>
                                      <option value="monthly">Monthly</option>
                                    </select>
                                  </div>
                                  {item.subscriptionType === 'alternate_days' && (
                                    <div>
                                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#115e59', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                        Every X Days
                                      </label>
                                      <input
                                        type="number"
                                        min="2"
                                        max="10"
                                        value={item.repeatCycleDays || 2}
                                        onChange={(e) => {
                                          const updatedItems = [...formData.items];
                                          updatedItems[index].repeatCycleDays = parseInt(e.target.value) || 2;
                                          setFormData(prev => ({ ...prev, items: updatedItems }));
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '10px 12px',
                                          border: '2px solid #5eead4',
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          fontWeight: '600',
                                          color: '#115e59',
                                          textAlign: 'center',
                                          backgroundColor: 'white'
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Day-wise Quantity Inputs (for Daily Subscription) */}
                              {item.subscriptionType === 'daily' && (
                                <div style={{ marginTop: '12px', padding: '14px', backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '8px' }}>
                                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#065f46', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <Calendar size={14} />
                                    Day-wise Quantities (Daily Subscription)
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIndex) => (
                                      <div key={day} style={{ textAlign: 'center' }}>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#065f46', marginBottom: '6px' }}>
                                          {day}
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.5"
                                          value={item.dayQuantities ? item.dayQuantities[dayIndex + 1] || 0 : 0}
                                          onChange={(e) => {
                                            const updatedItems = [...formData.items];
                                            if (!updatedItems[index].dayQuantities) {
                                              updatedItems[index].dayQuantities = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                                            }
                                            updatedItems[index].dayQuantities[dayIndex + 1] = parseFloat(e.target.value) || 0;
                                            setFormData(prev => ({ ...prev, items: updatedItems }));
                                          }}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '2px solid #86efac',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            textAlign: 'center',
                                            color: '#065f46',
                                            backgroundColor: 'white'
                                          }}
                                          placeholder="0"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#065f46', fontStyle: 'italic', textAlign: 'center', fontWeight: '600' }}>
                                    Total weekly quantity: {Object.values(item.dayQuantities || {}).reduce((sum, qty) => sum + parseFloat(qty || 0), 0).toFixed(1)}
                                  </div>
                                </div>
                              )}

                              {/* Subscription Pricing Breakdown */}
                              {subscriptionConfig.startDate && subscriptionConfig.endDate && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '12px 14px',
                                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                                  border: '2px solid #10b981',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  color: '#065f46'
                                }}>
                                  <div style={{ fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <Calendar size={14} />
                                    Subscription Pricing Breakdown
                                  </div>
                                  {(() => {
                                    const deliveryCount = calculateSubscriptionDeliveries(item, subscriptionConfig.startDate, subscriptionConfig.endDate);
                                    const effectiveQty = getEffectiveQuantity(item);
                                    const baseQty = parseFloat(item.quantity || 0);
                                    const unitPrice = parseFloat(item.unit_price || 0);
                                    const totalDays = Math.ceil((new Date(subscriptionConfig.endDate) - new Date(subscriptionConfig.startDate)) / (1000 * 60 * 60 * 24)) + 1;
                                    
                                    return (
                                      <div style={{ fontSize: '11px', lineHeight: '1.8', backgroundColor: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #86efac' }}>
                                        <div style={{ fontWeight: '600' }}>� Duration: {totalDays} days ({new Date(subscriptionConfig.startDate).toLocaleDateString()} to {new Date(subscriptionConfig.endDate).toLocaleDateString()})</div>
                                        {item.subscriptionType === 'daily' ? (
                                          <>
                                            <div style={{ fontWeight: '600' }}>� Total quantity (from day-wise): {deliveryCount.toFixed(1)} units</div>
                                            <div style={{ fontWeight: '600' }}>� Total amount: {deliveryCount.toFixed(1)} � {currencySymbol}{unitPrice.toFixed(2)} = {currencySymbol}{(effectiveQty * unitPrice).toFixed(2)}</div>
                                          </>
                                        ) : (
                                          <>
                                            <div style={{ fontWeight: '600' }}>� Delivery occurrences: {deliveryCount}</div>
                                            <div style={{ fontWeight: '600' }}>� Total amount: {deliveryCount} deliveries � {baseQty} qty � {currencySymbol}{unitPrice.toFixed(2)} = {currencySymbol}{(effectiveQty * unitPrice).toFixed(2)}</div>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* ? Add New Products/Services Button in Edit Mode */}
                  <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('products');
                        setShowManualProductModal(false);
                      }}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        padding: '12px 20px',
                        backgroundColor: '#f0f9ff',
                        border: '2px solid #0ea5e9',
                        borderRadius: '8px',
                        color: '#0369a1',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e0f2fe';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f9ff';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Plus size={18} />
                      Add Products from Catalog
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('services');
                        setShowManualProductModal(false);
                      }}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        padding: '12px 20px',
                        backgroundColor: '#fef3c7',
                        border: '2px solid #f59e0b',
                        borderRadius: '8px',
                        color: '#92400e',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fde68a';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Plus size={18} />
                      Add Services from Catalog
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setShowManualProductModal(true)}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        padding: '12px 20px',
                        backgroundColor: '#f0fdf4',
                        border: '2px solid #10b981',
                        borderRadius: '8px',
                        color: '#065f46',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#d1fae5';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Package size={18} />
                      Add Manual Product
                    </button>
                  </div>
                  
                  {/* ? Product/Service Selection UI in Edit Mode */}
                  {(activeTab === 'products' || activeTab === 'services') && (
                    <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#374151' }}>
                          {activeTab === 'products' ? '?? Select Products' : '??? Select Services'}
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveTab('products');
                            setSearchQuery('');
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#6b7280'
                          }}
                        >
                          Close
                        </button>
                      </div>
                      
                      {/* Search Bar */}
                      <div style={{ marginBottom: '12px' }}>
                        <input
                          type="text"
                          placeholder={`Search ${activeTab}...`}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      
                      {/* Items List */}
                      <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                        {(activeTab === 'products' ? filteredProducts : filteredServices).map((item) => {
                          const itemName = item.name || item.productName || item.product_name || item.serviceName || item.service_name || '';
                          const itemPrice = parseFloat(item.price || item.unit_price || item.mrp || 0);
                          const itemId = item.id;
                          const itemKey = `${activeTab}-${itemId}`;
                          const isSelected = selectedItemIds.has(itemKey);
                          
                          return (
                            <div
                              key={itemId}
                              onClick={() => handleCardClick(item, activeTab)}
                              style={{
                                padding: '12px',
                                backgroundColor: isSelected ? '#dbeafe' : 'white',
                                border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) e.currentTarget.style.borderColor = '#9ca3af';
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) e.currentTarget.style.borderColor = '#e5e7eb';
                              }}
                            >
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                                {itemName}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {currencySymbol}{itemPrice.toFixed(2)}
                              </div>
                              {isSelected && (
                                <div style={{ marginTop: '6px', fontSize: '11px', color: '#3b82f6', fontWeight: '600' }}>
                                  ? Added
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* ? Subscription Configuration Section (for subscription quotations) */}
                {isSubscriptionMode && (
                  <div className="edit-section">
                    <h3 className="edit-section-heading">?? Subscription Configuration</h3>
                    <div style={{ padding: '16px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '8px', marginBottom: '16px' }}>
                      <p style={{ margin: 0, fontSize: '13px', color: '#78350f', fontWeight: '600' }}>
                        ?? This is a subscription quotation. Subscription settings are preserved during edit.
                      </p>
                    </div>
                    <div className="edit-form-grid">
                      <div className="edit-form-group">
                        <label>Start Date *</label>
                        <input 
                          type="date" 
                          value={subscriptionConfig.startDate}
                          onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, startDate: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="edit-form-group">
                        <label>End Date *</label>
                        <input 
                          type="date" 
                          value={subscriptionConfig.endDate}
                          onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, endDate: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="edit-form-group">
                        <label>Billing Cycle</label>
                        <select 
                          value={subscriptionConfig.billingCycle}
                          onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, billingCycle: e.target.value }))}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      <div className="edit-form-group">
                        <label>Delivery Time Slot</label>
                        <select 
                          value={subscriptionConfig.deliveryTimeSlot}
                          onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, deliveryTimeSlot: e.target.value }))}
                        >
                          <option value="morning">Morning (6 AM - 10 AM)</option>
                          <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                          <option value="evening">Evening (4 PM - 8 PM)</option>
                        </select>
                      </div>
                      <div className="edit-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Delivery Instructions</label>
                        <textarea 
                          value={subscriptionConfig.deliveryInstructions}
                          onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, deliveryInstructions: e.target.value }))}
                          placeholder="Any special delivery instructions"
                          rows="2"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* -- Quotation Details -- */}
                <div className="edit-section">
                 <h3 className="edit-section-heading">Quotation Details</h3>
                  <div className="edit-form-grid">
                    <div className="edit-form-group">
                      <label>Quotation Date</label>
                      <input type="date" value={formData.quotationDate} onChange={(e) => handleInputChange('quotationDate', e.target.value)} />
                    </div>
                    <div className="edit-form-group">
                      <label>Valid Until *</label>
                      <input type="date" value={formData.validUntil} onChange={(e) => handleInputChange('validUntil', e.target.value)} required />
                    </div>
                    <div className="edit-form-group">
                      <label>Subtotal</label>
                      <input type="text" value={`${currencySymbol}${subtotal.toFixed(2)}`} disabled style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }} />
                    </div>
                    <div className="edit-form-group" style={{ backgroundColor: '#fef3c7', padding: '4px 6px', borderRadius: '4px' }}>
                      <label style={{ color: '#92400e', fontWeight: '700', fontSize: '11px' }}>GST AMOUNT</label>
                      <input
                        type="text"
                        value={`${currencySymbol}${gstAmount.toFixed(2)}`}
                        disabled
                        style={{ backgroundColor: '#fef3c7', cursor: 'not-allowed', fontWeight: '700', color: '#92400e', borderColor: '#fcd34d', fontSize: '13px' }}
                      />
                    </div>
                    <div className="edit-form-group">
                      <label>Discount Amount ({currencySymbol})</label>
                      <input type="number" min="0" step="0.01" value={formData.discountAmount} onChange={(e) => handleInputChange('discountAmount', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="edit-form-group">
                      <label>Shipping Amount ({currencySymbol})</label>
                      <input type="number" min="0" step="0.01" value={formData.shippingAmount} onChange={(e) => handleInputChange('shippingAmount', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="edit-form-group" style={{ backgroundColor: '#e0e7ff', padding: '4px 6px', borderRadius: '4px' }}>
                      <label style={{ color: '#4f46e5', fontWeight: '700', fontSize: '11px' }}>TOTAL AMOUNT</label>
                      <input
                        type="text"
                        value={`${currencySymbol}${totalAmount.toFixed(2)}`}
                        disabled
                        style={{ backgroundColor: '#e0e7ff', cursor: 'not-allowed', fontWeight: '700', color: '#4f46e5', borderColor: '#c7d2fe', fontSize: '13px' }}
                      />
                    </div>
                    <div className="edit-form-group">
                      <label>Payment Terms</label>
                      <input type="text" value={formData.paymentTerms} onChange={(e) => handleInputChange('paymentTerms', e.target.value)} placeholder="Due within 30 days" />
                    </div>
                    <div className="edit-form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Notes</label>
                      <textarea value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} placeholder="Additional notes" rows="2" />
                    </div>
                  </div>
                </div>

                </div>

                <div className="edit-form-actions">
                  <button type="button" onClick={onClose} className="btn-cancel">Cancel</button>
                  <button type="submit" className="btn-save" disabled={loading}>
                    {loading ? 'Saving...' : 'Update Quotation'}
                  </button>
                </div>

              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // CREATE MODE
  // ==========================================================================
  return (
    <>
      {/* -- MANUAL PRODUCT MODAL ---------------------------------------------
          IMPORTANT: Rendered as inline JSX directly here � NOT as a separate
          sub-component function. Defining it as const ManualProductModal = () =>
          inside the parent causes React to remount it on every keystroke
          (new component identity each render), which resets input focus.
      ------------------------------------------------------------------------ */}
      {showManualProductModal && (
        <div className="manual-product-modal-overlay" onClick={() => setShowManualProductModal(false)}>
          <div className="manual-product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manual-product-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Package size={22} />
                <div>
                  <h3>Add Product Manually</h3>
                  <p>Create and add a new product to quotation</p>
                </div>
              </div>
              <button
                onClick={() => setShowManualProductModal(false)}
                className="manual-product-close-btn"
                type="button"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleAddManualProduct} className="manual-product-form">
              <div className="manual-product-form-grid">
                <div className="manual-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Product Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Custom Widget"
                    value={manualProductData.productName}
                    onChange={(e) => setManualProductData(prev => ({
                      ...prev,
                      productName: e.target.value
                    }))}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="manual-form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="e.g., 5"
                    value={manualProductData.quantity}
                    onChange={(e) => setManualProductData(prev => ({
                      ...prev,
                      quantity: e.target.value === '' ? '' : parseFloat(e.target.value)
                    }))}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="manual-form-group">
                  <label>Price ({currencySymbol}) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g., 500.00"
                    value={manualProductData.price}
                    onChange={(e) => setManualProductData(prev => ({
                      ...prev,
                      price: e.target.value
                    }))}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="manual-form-group">
                  <label>Unit *</label>
                  <input
                    type="text"
                    placeholder="e.g., Piece, Box, Kg, Liter"
                    value={manualProductData.unit}
                    onChange={(e) => setManualProductData(prev => ({
                      ...prev,
                      unit: e.target.value
                    }))}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="manual-form-group">
                  <label>GST Rate (%) *</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g., 18"
                    value={manualProductData.gstRate}
                    onChange={(e) => setManualProductData(prev => ({
                      ...prev,
                      gstRate: e.target.value
                    }))}
                    required
                    autoComplete="off"
                  />
                </div>

                <div className="manual-form-group">
                  <label>Category *</label>
                  <input
                    type="text"
                    placeholder="e.g., Electronics, Food, Software"
                    value={manualProductData.category}
                    onChange={(e) => setManualProductData(prev => ({
                      ...prev,
                      category: e.target.value
                    }))}
                    required
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="manual-product-form-actions">
                <button
                  type="button"
                  onClick={() => setShowManualProductModal(false)}
                  className="btn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-modal-add"
                  disabled={savingProduct}
                >
                  {savingProduct ? 'Saving to catalog...' : 'Add to Quotation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="quotation-modal-overlay" onClick={onClose}>
        <div className="quotation-modal" onClick={(e) => e.stopPropagation()}>

          <div className="quotation-modal-header">
            <div>
              <h2>{editQuotation ? 'Edit Quotation' : 'Create New Quotation'}</h2>
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    // Prevent toggling subscription mode when editing
                    if (editQuotation) {
                      toast.error('Cannot change quotation type when editing');
                      return;
                    }
                    setIsSubscriptionMode(!isSubscriptionMode);
                  }}
                  disabled={editQuotation} // Disable toggle when editing
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: isSubscriptionMode ? '2px solid #10b981' : '2px solid #d1d5db',
                    background: isSubscriptionMode ? '#ecfdf5' : 'white',
                    color: isSubscriptionMode ? '#059669' : '#6b7280',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: editQuotation ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    opacity: editQuotation ? 0.6 : 1
                  }}
                  title={editQuotation ? 'Cannot change quotation type when editing' : 'Toggle subscription mode'}
                >
                  <RefreshCw size={16} />
                  {isSubscriptionMode ? 'Subscription Quote' : 'Regular Quote'}
                </button>
                {isSubscriptionMode && (
                  <span style={{ fontSize: '12px', color: '#059669', fontWeight: '500' }}>
                    <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Will convert to subscription order
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="quotation-close-btn"><X size={24} /></button>
          </div>

          {/* =============== SUBSCRIPTION CONFIGURATION PANEL =============== */}
          {isSubscriptionMode && (
            <div style={{
              padding: '10px 20px 8px',
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              borderBottom: '2px solid #10b981',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <RefreshCw size={14} style={{ color: '#059669' }} />
                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#065f46' }}>
                  Subscription Configuration
                </h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#065f46', marginBottom: '3px' }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required={isSubscriptionMode}
                    min={(() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow.toISOString().split('T')[0];
                    })()}
                    value={subscriptionConfig.startDate}
                    onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '5px 6px',
                      border: '1px solid #86efac',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#065f46', marginBottom: '3px' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    required={isSubscriptionMode}
                    min={(() => {
                      if (!subscriptionConfig.startDate) {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        return tomorrow.toISOString().split('T')[0];
                      }
                      const dayAfterStart = new Date(subscriptionConfig.startDate);
                      dayAfterStart.setDate(dayAfterStart.getDate() + 1);
                      return dayAfterStart.toISOString().split('T')[0];
                    })()}
                    value={subscriptionConfig.endDate}
                    onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '5px 6px',
                      border: '1px solid #86efac',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#065f46', marginBottom: '3px' }}>
                    Delivery Time Slot
                  </label>
                  <select
                    value={subscriptionConfig.deliveryTimeSlot}
                    onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, deliveryTimeSlot: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '5px 6px',
                      border: '1px solid #86efac',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="morning">Morning (6AM - 10AM)</option>
                    <option value="afternoon">Afternoon (12PM - 4PM)</option>
                    <option value="evening">Evening (4PM - 8PM)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#065f46', marginBottom: '3px' }}>
                    Billing Cycle
                  </label>
                  <select
                    value={subscriptionConfig.billingCycle}
                    onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, billingCycle: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '5px 6px',
                      border: '1px solid #86efac',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="half_yearly">Half-Yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#065f46', marginBottom: '3px' }}>
                    Delivery Instructions
                  </label>
                  <input
                    type="text"
                    value={subscriptionConfig.deliveryInstructions}
                    onChange={(e) => setSubscriptionConfig(prev => ({ ...prev, deliveryInstructions: e.target.value }))}
                    placeholder="Gate code, landmark, etc."
                    style={{
                      width: '100%',
                      padding: '5px 6px',
                      border: '1px solid #86efac',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Lead Tracking Banner */}
          {leadData && (
            <div style={{
              padding: '12px 20px',
              background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)',
              borderBottom: '2px solid #0ea5e9',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: '#0ea5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                flexShrink: 0
              }}>
                {leadData.customer_name ? leadData.customer_name.charAt(0).toUpperCase() : '??'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#0c4a6e', marginBottom: '2px' }}>
                  Creating quotation for lead - Details pre-filled
                </div>
                <div style={{ fontSize: '12px', color: '#075985' }}>
                  <strong>{leadData.customer_name || 'Lead'}</strong>
                  {leadData.mobile_number && ` � ${getDisplayPhoneNumber(leadData.mobile_number)}`}
                  {leadData.email && ` � ${leadData.email}`}
                  <span style={{ marginLeft: '8px', opacity: 0.8 }}>� You can add as new customer or use existing</span>
                </div>
              </div>
              <div style={{
                padding: '4px 10px',
                background: '#0ea5e9',
                color: 'white',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Linked to Lead
              </div>
            </div>
          )}

          <div className="quotation-modal-content">
            <form onSubmit={handleSubmit}>
              <div className="quotation-layout">

                {/* LEFT PANEL */}
                <div className="quotation-forms-section" style={{ overflowX: 'visible' }}>

                  {/* -- Select Customer � Searchable -- */}
                  <div className="quotation-form-card" style={{ overflow: 'visible' }}>
                    <div className="quotation-section-header">
                      <div className="header-content">
                        <User size={18} />
                        <span>Select Customer *</span>
                      </div>
                    </div>
                    <div style={{ padding: '16px', overflow: 'visible' }}>

                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Select Customer *
                      </div>

                      <div ref={dropdownWrapperRef} style={{ position: 'relative', zIndex: 1000 }}>
                        <div
                          onClick={() => {
                            const willOpen = !showCustomerDropdown;
                            setShowCustomerDropdown(willOpen);
                            // Load default customers when opening if none loaded yet
                            if (willOpen && customers.length === 0 && !customerSearchQuery.trim()) {
                              searchCustomers('');
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            border: `1px solid ${showCustomerDropdown ? '#6366f1' : (leadData ? '#0ea5e9' : '#d1d5db')}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            background: leadData ? '#f0f9ff' : 'white',
                            boxShadow: showCustomerDropdown ? '0 0 0 3px rgba(99,102,241,0.1)' : 'none',
                            userSelect: 'none',
                            transition: 'border-color 0.2s'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                            <Search size={15} style={{ color: '#9ca3af', flexShrink: 0 }} />
                            {selectedCustomer ? (
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {selectedCustomer.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                  {selectedCustomer.phone}{selectedCustomer.email ? ` � ${selectedCustomer.email}` : ''}
                                </div>
                              </div>
                            ) : leadData ? (
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#0369a1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {leadData.customer_name || 'Lead Customer'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#0284c7' }}>
                                  Pre-filled from lead � Click to search or add new customer
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '13px', color: '#9ca3af' }}>Search customers by name, phone, or email...</span>
                            )}
                          </div>
                          <ChevronDown
                            size={16}
                            style={{
                              color: '#9ca3af',
                              flexShrink: 0,
                              transform: showCustomerDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }}
                          />
                        </div>

                        {showCustomerDropdown && (() => {
                          const rect = dropdownWrapperRef.current?.getBoundingClientRect();
                          if (!rect) return null;
                          return (
                            <div style={{
                              position: 'fixed',
                              top: `${rect.bottom + 4}px`,
                              left: `${rect.left}px`,
                              width: `${rect.width}px`,
                              background: 'white',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              boxShadow: '0 10px 32px rgba(0,0,0,0.15)',
                              zIndex: 99999,
                              overflow: 'hidden',
                              maxHeight: '280px'
                            }}>
                              <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', position: 'relative' }}>
                                <Search size={14} style={{ position: 'absolute', left: '22px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                                <input
                                  type="text"
                                  placeholder="Search..."
                                  value={customerSearchQuery}
                                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                                  autoFocus
                                  style={{
                                    width: '100%',
                                    padding: '7px 10px 7px 30px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontFamily: 'inherit',
                                    outline: 'none',
                                    background: '#f9fafb',
                                    boxSizing: 'border-box'
                                  }}
                                  onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.background = 'white'; }}
                                  onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; }}
                                />
                              </div>

                              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                {loadingCustomers ? (
                                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
                                    Searching...
                                  </div>
                                ) : customers.length > 0 ? (
                                  customers.map(customer => (
                                    <div
                                      key={customer.id}
                                      onMouseDown={(e) => { e.preventDefault(); handleSelectCustomer(customer); }}
                                      style={{
                                        padding: '10px 16px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f9fafb',
                                        background: selectedCustomer?.id === customer.id ? '#f5f3ff' : 'white',
                                        borderLeft: selectedCustomer?.id === customer.id ? '3px solid #6366f1' : '3px solid transparent',
                                      }}
                                      onMouseEnter={e => { if (selectedCustomer?.id !== customer.id) e.currentTarget.style.background = '#fafafa'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = selectedCustomer?.id === customer.id ? '#f5f3ff' : 'white'; }}
                                    >
                                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', marginBottom: '3px' }}>
                                        {customer.name}
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                        {customer.phone}{customer.email ? ` � ${customer.email}` : ''}
                                      </div>
                                    </div>
                                  ))
                                ) : customerSearchQuery.trim().length > 0 ? (
                                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
                                    No customers found
                                  </div>
                                ) : (
                                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
                                    {loadingCustomers ? 'Loading customers...' : 'No customers linked to your account yet'}
                                  </div>
                                )}
                              </div>
                              
                              {/* -- Add New Customer Button -- */}
                              <div style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowCustomerDropdown(false);
                                    handleOpenAddCustomerModal();
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    backgroundColor: '#f0f9ff',
                                    border: '2px dashed #0ea5e9',
                                    borderRadius: '4px',
                                    color: '#0369a1',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseOver={(e) => { 
                                    e.currentTarget.style.backgroundColor = '#e0f2fe';
                                    e.currentTarget.style.borderColor = '#0284c7';
                                  }}
                                  onMouseOut={(e) => { 
                                    e.currentTarget.style.backgroundColor = '#f0f9ff';
                                    e.currentTarget.style.borderColor = '#0ea5e9';
                                  }}
                                >
                                  <Plus size={14} />
                                  Add New Customer
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* -- Customer Addresses -- */}
                  <div className="quotation-form-card">
                    <div 
                      className="quotation-section-header"
                      onClick={() => toggleSection('customerInfo')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div className="header-content">
                        <MapPin size={18} />
                        <span>Customer Addresses</span>
                        <ChevronDown 
                          size={18} 
                          style={{ 
                            transition: 'transform 0.2s',
                            transform: collapsedSections.customerInfo ? 'rotate(-90deg)' : 'rotate(0deg)',
                            marginLeft: '8px'
                          }}
                        />
                      </div>
                    </div>
                    {!collapsedSections.customerInfo && (
                      <div style={{ padding: '16px' }}>
                      
                      {/* Basic Customer Info */}
                      <div className="quotation-form-grid" style={{ marginBottom: '20px' }}>
                        <div className="form-group">
                          <label>Customer Name *</label>
                          <input type="text" value={formData.customerName} onChange={(e) => handleInputChange('customerName', e.target.value)} placeholder="Enter customer name" required />
                        </div>
                      <div className="form-group">
                          <label>Phone *</label>
                        <input
                            type="tel"
                            value={formData.customerPhone}
                            onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                            placeholder="10-digit number"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input type="email" value={formData.customerEmail} onChange={(e) => handleInputChange('customerEmail', e.target.value)} placeholder="customer@email.com" />
                        </div>
                      </div>

                      {/* Billing Address Section */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '4px', height: '16px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
                            Billing Address
                          </label>
                          {customerAddresses.length > 0 && (
                            <select
                              onChange={(e) => {
                                const addr = customerAddresses.find(a => a.id === e.target.value);
                                if (addr) handleSelectBillingAddress(addr);
                              }}
                              value={formData.billingAddress.id || ''}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="">Select Address</option>
                              {customerAddresses.map(addr => (
                                <option key={addr.id} value={addr.id}>
                                  {addr.address_label || addr.address_type || 'Address'} - {addr.city}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        
                        <div className="quotation-form-grid">
                          <div className="form-group">
                            <label>Flat/Plot Number</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.flat_plot_number} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, flat_plot_number: e.target.value }
                              }))} 
                              placeholder="Flat/Plot No." 
                            />
                          </div>
                          <div className="form-group">
                            <label>Floor/Lane No.</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.floor_lane_no} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, floor_lane_no: e.target.value }
                              }))} 
                              placeholder="Floor/Lane" 
                            />
                          </div>
                          <div className="form-group">
                            <label>Building/Colony Name</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.building_colony_name} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, building_colony_name: e.target.value }
                              }))} 
                              placeholder="Building/Colony" 
                            />
                          </div>
                          <div className="form-group">
                            <label>Society/Area Name</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.society_area_name} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, society_area_name: e.target.value }
                              }))} 
                              placeholder="Society/Area" 
                            />
                          </div>
                          <div className="form-group full-width">
                            <label>Full Address</label>
                            <textarea 
                              value={formData.billingAddress.address} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, address: e.target.value }
                              }))} 
                              placeholder="Complete billing address" 
                              rows="2" 
                            />
                          </div>
                          <div className="form-group">
                            <label>City</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.city} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, city: e.target.value }
                              }))} 
                              placeholder="City" 
                            />
                          </div>
                          <div className="form-group">
                            <label>State</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.state} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, state: e.target.value }
                              }))} 
                              placeholder="State" 
                            />
                          </div>
                          <div className="form-group">
                            <label>Pincode</label>
                            <input 
                              type="text" 
                              value={formData.billingAddress.pincode} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                billingAddress: { ...prev.billingAddress, pincode: e.target.value }
                              }))} 
                              placeholder="Pincode" 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Shipping Address Section */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '4px', height: '16px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
                            Shipping Address
                          </label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  shippingAddress: { ...prev.billingAddress }
                                }));
                                toast.success('Copied billing address to shipping');
                              }}
                              style={{
                                padding: '4px 10px',
                                fontSize: '11px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                fontWeight: '500',
                                color: '#6b7280'
                              }}
                            >
                              Same as Billing
                            </button>
                            {customerAddresses.length > 0 && (
                              <select
                                onChange={(e) => {
                                  const addr = customerAddresses.find(a => a.id === e.target.value);
                                  if (addr) handleSelectShippingAddress(addr);
                                }}
                                value={formData.shippingAddress.id || ''}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  backgroundColor: 'white'
                                }}
                              >
                                <option value="">Select Address</option>
                                {customerAddresses.map(addr => (
                                  <option key={addr.id} value={addr.id}>
                                    {addr.address_label || addr.address_type || 'Address'} - {addr.city}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                        
                        <div className="quotation-form-grid">
                          <div className="form-group">
                            <label>Flat/Plot Number</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.flat_plot_number} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, flat_plot_number: e.target.value }
                              }))} 
                              placeholder="Flat/Plot No." 
                            />
                          </div>
                          <div className="form-group">
                            <label>Floor/Lane No.</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.floor_lane_no} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, floor_lane_no: e.target.value }
                              }))} 
                              placeholder="Floor/Lane" 
                            />
                          </div>
                          <div className="form-group">
                            <label>Building/Colony Name</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.building_colony_name} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, building_colony_name: e.target.value }
                              }))} 
                              placeholder="Building/Colony" 
                            />
                          </div>
                          <div className="form-group">
                            <label>Society/Area Name</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.society_area_name} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, society_area_name: e.target.value }
                              }))} 
                              placeholder="Society/Area" 
                            />
                          </div>
                          <div className="form-group full-width">
                            <label>Full Address</label>
                            <textarea 
                              value={formData.shippingAddress.address} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, address: e.target.value }
                              }))} 
                              placeholder="Complete shipping/delivery address" 
                              rows="2" 
                            />
                          </div>
                          <div className="form-group">
                            <label>City</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.city} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, city: e.target.value }
                              }))} 
                              placeholder="City" 
                            />
                          </div>
                          <div className="form-group">
                            <label>State</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.state} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, state: e.target.value }
                              }))} 
                              placeholder="State" 
                            />
                          </div>
                          <div className="form-group">
                            <label>Pincode</label>
                            <input 
                              type="text" 
                              value={formData.shippingAddress.pincode} 
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shippingAddress: { ...prev.shippingAddress, pincode: e.target.value }
                              }))} 
                              placeholder="Pincode" 
                            />
                          </div>
                        </div>
                      </div>

                    </div>
                    )}
                  </div>

                  {/* -- Selected Items -- */}
                  <div className="quotation-form-card">
                    <div className="quotation-section-header">
                      <div className="header-content">
                        <ShoppingCart size={18} />
                        <span>Selected Items ({formData.items.length})</span>
                      </div>
                    </div>
                    <div className="selected-items-container">
                      {formData.items.length === 0 ? (
                        <div className="empty-state">
                          <ShoppingCart size={28} />
                          <p>No items selected</p>
                          <small>Select products or services from the right</small>
                        </div>
                      ) : (
                        <div className="items-list">
                          {formData.items.map((item, index) => (
                            <div key={index} className="quotation-item-card">
                              {/* Header Row: Product Name, Category, Type */}
                              <div className="item-header-row">
                                <div className="item-name-section">
                                  <h4 className="item-name">{item.item_name}</h4>
                                  {item.item_details?.category && (
                                    <div className="item-category">
                                      <Tag size={12} />
                                      <span>{item.item_details.category}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="item-type-badge">
                                  <Package size={12} />
                                  {item.item_type}
                                </div>
                              </div>

                              {/* Main Content Grid: Quantity, Price, Discount, Total */}
                              <div className="item-content-grid">
                                {/* Quantity Control */}
                                <div className="item-field-group">
                                  <label className="field-label">Quantity</label>
                                  <div className="qty-control-wrapper">
                                    <button 
                                      type="button" 
                                      onClick={() => handleItemChange(index, 'quantity', Math.max(1, parseFloat(item.quantity) - 1))} 
                                      className="qty-btn qty-btn-minus"
                                    >
                                      -
                                    </button>
                                    <input 
                                      type="number" 
                                      min="1" 
                                      value={item.quantity} 
                                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 1)} 
                                      className="qty-input-field" 
                                    />
                                    <button 
                                      type="button" 
                                      onClick={() => handleItemChange(index, 'quantity', parseFloat(item.quantity) + 1)} 
                                      className="qty-btn qty-btn-plus"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                {/* Unit Price */}
                                <div className="item-field-group">
                                  <label className="field-label">Unit Price</label>
                                  <div className="price-value">
                                    {currencySymbol}{(parseFloat(item.unit_price || 0)).toFixed(2)}
                                  </div>
                                </div>

                                {/* Discount Percent */}
                                <div className="item-field-group">
                                  <label className="field-label">Discount %</label>
                                  <input 
                                    type="number" 
                                    min="0" 
                                    max="100" 
                                    step="0.01" 
                                    value={item.discount_percent || 0} 
                                    onChange={(e) => handleItemChange(index, 'discount_percent', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))} 
                                    className="qty-input-field" 
                                    placeholder="0"
                                  />
                                </div>

                                {/* Total Price */}
                                <div className="item-field-group">
                                  <label className="field-label">Total (excl. GST)</label>
                                  <div className="total-value">
                                    {currencySymbol}{(() => {
                                      const effectiveQty = getEffectiveQuantity(item);
                                      const itemGross = effectiveQty * parseFloat(item.unit_price || 0);
                                      const itemDiscount = (itemGross * parseFloat(item.discount_percent || 0)) / 100;
                                      const itemTaxable = itemGross - itemDiscount;
                                      return itemTaxable.toFixed(2);
                                    })()}
                                  </div>
                                </div>

                                {/* Remove Button */}
                                <div className="item-field-group">
                                  <label className="field-label">&nbsp;</label>
                                  <button 
                                    type="button" 
                                    onClick={() => handleRemoveItem(index)} 
                                    className="item-remove-button"
                                    title="Remove item"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>

                              {/* =============== DAY-WISE QUANTITY INPUTS (SUBSCRIPTION MODE) =============== */}
                              {isSubscriptionMode && item.subscriptionType === 'daily' && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '12px',
                                  background: '#f0fdf4',
                                  border: '1px solid #86efac',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#065f46', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Calendar size={14} />
                                    Day-wise Quantities (Daily Subscription)
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIndex) => (
                                      <div key={day} style={{ textAlign: 'center' }}>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                                          {day}
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.5"
                                          value={item.dayQuantities ? item.dayQuantities[dayIndex + 1] || 0 : 0}
                                          onChange={(e) => {
                                            const updatedItems = [...formData.items];
                                            if (!updatedItems[index].dayQuantities) {
                                              updatedItems[index].dayQuantities = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                                            }
                                            updatedItems[index].dayQuantities[dayIndex + 1] = parseFloat(e.target.value) || 0;
                                            setFormData(prev => ({ ...prev, items: updatedItems }));
                                          }}
                                          style={{
                                            width: '100%',
                                            padding: '6px',
                                            border: '1px solid #86efac',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            textAlign: 'center'
                                          }}
                                          placeholder="0"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#065f46', fontStyle: 'italic' }}>
                                    Total weekly quantity: {Object.values(item.dayQuantities || {}).reduce((sum, qty) => sum + parseFloat(qty || 0), 0).toFixed(1)}
                                  </div>
                                </div>
                              )}

                              {/* Subscription Type Selector (Subscription Mode) */}
                              {isSubscriptionMode && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '10px',
                                  background: '#f0fdfa',
                                  border: '1px solid #5eead4',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: item.subscriptionType === 'alternate_days' ? '2fr 1fr' : '1fr', gap: '10px' }}>
                                    <div>
                                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#115e59', marginBottom: '6px' }}>
                                        Subscription Type
                                      </label>
                                      <select
                                        value={item.subscriptionType || 'daily'}
                                        onChange={(e) => {
                                          const updatedItems = [...formData.items];
                                          updatedItems[index].subscriptionType = e.target.value;
                                          // Initialize day quantities for daily type
                                          if (e.target.value === 'daily' && !updatedItems[index].dayQuantities) {
                                            updatedItems[index].dayQuantities = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
                                          }
                                          // Initialize repeat cycle for alternate days
                                          if (e.target.value === 'alternate_days' && !updatedItems[index].repeatCycleDays) {
                                            updatedItems[index].repeatCycleDays = 2;
                                          }
                                          setFormData(prev => ({ ...prev, items: updatedItems }));
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '8px',
                                          border: '1px solid #5eead4',
                                          borderRadius: '4px',
                                          fontSize: '13px'
                                        }}
                                      >
                                        <option value="daily">Daily (with day-wise quantities)</option>
                                        <option value="alternate_days">Alternate Days</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                      </select>
                                    </div>
                                    {item.subscriptionType === 'alternate_days' && (
                                      <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#115e59', marginBottom: '6px' }}>
                                          Every X Days
                                        </label>
                                        <input
                                          type="number"
                                          min="2"
                                          max="10"
                                          value={item.repeatCycleDays || 2}
                                          onChange={(e) => {
                                            const updatedItems = [...formData.items];
                                            updatedItems[index].repeatCycleDays = parseInt(e.target.value) || 2;
                                            setFormData(prev => ({ ...prev, items: updatedItems }));
                                          }}
                                          style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #5eead4',
                                            borderRadius: '4px',
                                            fontSize: '13px',
                                            textAlign: 'center'
                                          }}
                                          placeholder="2"
                                        />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Subscription Calculation Info */}
                                  {subscriptionConfig.startDate && subscriptionConfig.endDate && (
                                    <div style={{
                                      marginTop: '10px',
                                      padding: '8px 10px',
                                      background: '#ecfdf5',
                                      border: '1px solid #6ee7b7',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      color: '#065f46'
                                    }}>
                                      <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Calendar size={12} />
                                        Pricing Breakdown
                                      </div>
                                      {(() => {
                                        const deliveryCount = calculateSubscriptionDeliveries(item, subscriptionConfig.startDate, subscriptionConfig.endDate);
                                        const effectiveQty = getEffectiveQuantity(item);
                                        const baseQty = parseFloat(item.quantity || 0);
                                        const unitPrice = parseFloat(item.unit_price || 0);
                                        const totalDays = Math.ceil((new Date(subscriptionConfig.endDate) - new Date(subscriptionConfig.startDate)) / (1000 * 60 * 60 * 24)) + 1;
                                        
                                        return (
                                          <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
                                            <div>� Duration: {totalDays} days ({new Date(subscriptionConfig.startDate).toLocaleDateString()} to {new Date(subscriptionConfig.endDate).toLocaleDateString()})</div>
                                            {item.subscriptionType === 'daily' ? (
                                              <>
                                                <div>� Total deliveries (from day-wise qty): {deliveryCount.toFixed(1)} units</div>
                                                <div>� Calculation: {deliveryCount.toFixed(1)} � {currencySymbol}{unitPrice.toFixed(2)} � {baseQty} = {currencySymbol}{(effectiveQty * unitPrice).toFixed(2)}</div>
                                              </>
                                            ) : (
                                              <>
                                                <div>� Delivery occurrences: {deliveryCount}</div>
                                                <div>� Calculation: {deliveryCount} deliveries � {baseQty} qty � {currencySymbol}{unitPrice.toFixed(2)} = {currencySymbol}{(effectiveQty * unitPrice).toFixed(2)}</div>
                                              </>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* GST and Discount Information Row */}
                              <div className="item-gst-row">
                                <div className="gst-info">
                                  <span className="gst-label">GST Rate:</span>
                                  <span className="gst-rate">{(() => {
                                    const rate = item.product_gst_rate !== undefined && item.product_gst_rate !== null ? parseFloat(item.product_gst_rate) : 0;
                                    return rate.toFixed(2);
                                  })()}%</span>
                                </div>
                                <div className="gst-amount">
                                  <span className="gst-label">GST Amount:</span>
                                  <span className="gst-value">
                                    {currencySymbol}{(() => {
                                      const effectiveQty = getEffectiveQuantity(item);
                                      const itemGross = effectiveQty * parseFloat(item.unit_price || 0);
                                      const itemDiscount = (itemGross * parseFloat(item.discount_percent || 0)) / 100;
                                      const itemTaxable = itemGross - itemDiscount;
                                      const gstRate = item.product_gst_rate !== undefined && item.product_gst_rate !== null ? parseFloat(item.product_gst_rate) : 0;
                                      return ((itemTaxable * gstRate) / 100).toFixed(2);
                                    })()}
                                  </span>
                                </div>
                                {parseFloat(item.discount_percent || 0) > 0 && (
                                  <div className="gst-amount">
                                    <span className="gst-label">Discount:</span>
                                    <span className="gst-value" style={{ color: '#dc2626' }}>
                                      ?{(() => {
                                        const effectiveQty = getEffectiveQuantity(item);
                                        return ((effectiveQty * parseFloat(item.unit_price || 0) * parseFloat(item.discount_percent || 0)) / 100).toFixed(2);
                                      })()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* -- Quotation Details -- */}
                  <div className="quotation-form-card">
                    <div className="quotation-section-header">
                      <div className="header-content"><DollarSign size={18} /><span>Quotation Details</span></div>
                    </div>
                    <div className="quotation-form-grid">
                      <div className="pricing-display">
                        <div className="price-row">
                          <span>Subtotal (excl. GST)</span>
                          <strong>{currencySymbol}{subtotal.toFixed(2)}</strong>
                        </div>
                        <div className="price-row" style={{
                          backgroundColor: '#fef3c7',
                          padding: '10px 12px',
                          borderRadius: '4px',
                          marginTop: '8px',
                          marginBottom: '8px',
                          fontWeight: 'bold',
                          border: '2px solid #fcd34d'
                        }}>
                          <span style={{ fontWeight: '700', color: '#92400e', fontSize: '13px' }}>GST AMOUNT</span>
                          <strong style={{ color: '#92400e', fontSize: '15px' }}>{currencySymbol}{gstAmount.toFixed(2)}</strong>
                        </div>
                        {Object.keys(gstCalculation.breakdown).length > 0 && (
                          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '6px', padding: '6px 10px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>GST Breakdown:</div>
                            {Object.entries(gstCalculation.breakdown).map(([rate, amount]) => (
                              <div key={rate} style={{ marginLeft: '8px', marginTop: '2px' }}>
                                {rate}%: {currencySymbol}{amount.toFixed(2)}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="form-group">
                          <label>Discount Amount ({currencySymbol})</label>
                          <input type="number" min="0" step="0.01" value={formData.discountAmount} onChange={(e) => handleInputChange('discountAmount', parseFloat(e.target.value) || 0)} />
                        </div>
                        {parseFloat(formData.discountAmount) > 0 && (
                          <div className="price-row"><span>Discount</span><strong>{currencySymbol}{discountAmount.toFixed(2)}</strong></div>
                        )}
                        <div className="form-group">
                          <label>Shipping ({currencySymbol})</label>
                          <input type="number" min="0" step="0.01" value={formData.shippingAmount} onChange={(e) => handleInputChange('shippingAmount', parseFloat(e.target.value) || 0)} />
                        </div>
                        {parseFloat(formData.shippingAmount) > 0 && (
                          <div className="price-row"><span>Shipping</span><strong>{currencySymbol}{shippingAmount.toFixed(2)}</strong></div>
                        )}
                        <div className="price-row total">
                          <span>Total</span>
                          <strong>{currencySymbol}{totalAmount.toFixed(2)}</strong>
                        </div>
                      </div>

                      <div className="form-group full-width">
                        <label>Payment Terms</label>
                        <input type="text" value={formData.paymentTerms} onChange={(e) => handleInputChange('paymentTerms', e.target.value)} placeholder="Due within 30 days" />
                      </div>
                      <div className="form-group">
                        <label>Quotation Date</label>
                        <input type="date" value={formData.quotationDate} onChange={(e) => handleInputChange('quotationDate', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Valid Until *</label>
                        <input type="date" value={formData.validUntil} onChange={(e) => handleInputChange('validUntil', e.target.value)} required />
                      </div>
                      <div className="form-group full-width">
                        <label>Notes</label>
                        <textarea value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} placeholder="Additional notes" rows="2" />
                      </div>
                    </div>
                  </div>

                  {/* -- Actions -- */}
                  <div className="form-actions">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Create Quotation'}
                    </button>
                  </div>

                </div>

                {/* RIGHT PANEL � Catalogue */}
                <div className="quotation-catalogue-section">
                  <div className="catalogue-tabs">
                    <button type="button" className={`catalogue-tab ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
                      Products
                    </button>
                    <button type="button" className={`catalogue-tab ${activeTab === 'services' ? 'active' : ''}`} onClick={() => setActiveTab('services')}>
                      Services
                    </button>
                  </div>

                  <div className="catalogue-search" style={{ position: 'relative', display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowManualProductModal(true)}
                      className="btn-add-manual-product"
                      title="Add product manually"
                    >
                      <Plus size={18} />
                    </button>
                  </div>

                  <div className="catalogue-grid">
                    {activeTab === 'products' ? (
                      filteredProducts.length > 0 ? (
                        filteredProducts.map(product => {
                          const itemKey = `products-${product.id}`;
                          const isSelected = selectedItemIds.has(itemKey);
                          return (
                            <div
                              key={product.id}
                              className={`quotation-product-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleCardClick(product, 'products')}
                            >
                              <div className="quotation-product-image">
                                {product.image ? (
                                  <img src={product.image} alt={product.name} />
                                ) : (
                                  <div className="quotation-product-placeholder">
                                    <ShoppingCart size={40} />
                                  </div>
                                )}
                              </div>
                              <div className="quotation-product-info">
                                <h4 className="quotation-product-name">{product.name || product.productName || 'Product'}</h4>
                                <p className="quotation-product-price">{currencySymbol}{(parseFloat(product.mrp || product.price || 0)).toFixed(2)}</p>
                                <p className="quotation-product-gst">
                                  <strong>GST:</strong> {(parseFloat(product.gst_rate || 0)).toFixed(2)}%
                                </p>
                               {product.category && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  fontSize: '10px',
                                  fontWeight: '700',
                                   color: '#111827',

                                  background: '#ede9fe',
                                  padding: '2px 7px',
                                  borderRadius: '20px',
                                  flexShrink: 0,
                                  width: 'fit-content',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  <Tag size={10} />
                                  {product.category}
                                </span>
                              )}
                              <p className="quotation-product-unit">{product.unit || 'Unit'}</p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '30px 20px', color: '#999', fontSize: '12px' }}>
                          No products found
                        </div>
                      )
                    ) : (
                      filteredServices.length > 0 ? (
                        filteredServices.map(service => {
                          const itemKey = `services-${service.id}`;
                          const isSelected = selectedItemIds.has(itemKey);
                          return (
                            <div
                              key={service.id}
                              className={`quotation-product-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleCardClick(service, 'services')}
                            >
                              <div className="quotation-product-image">
                                {service.image ? (
                                  <img src={service.image} alt={service.name} />
                                ) : (
                                  <div className="quotation-product-placeholder">
                                    <FileText size={40} />
                                  </div>
                                )}
                              </div>
                              <div className="quotation-product-info">
                                <h4 className="quotation-product-name">{service.name || service.serviceName || 'Service'}</h4>
                                <p className="quotation-product-price">{currencySymbol}{(parseFloat(service.mrp || service.price || 0)).toFixed(2)}</p>
                                <p className="quotation-product-gst">
                                  <strong>GST:</strong> {(parseFloat(service.gst_rate || 0)).toFixed(2)}%
                                </p>
                              {service.category && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  fontSize: '10px',
                                  fontWeight: '700',
                                  color: '#111827',

                                  background: '#ede9fe',
                                  padding: '2px 7px',
                                  borderRadius: '20px',
                                  flexShrink: 0,
                                  width: 'fit-content',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  <Tag size={10} />
                                  {service.category}
                                </span>
                              )}
                              <p className="quotation-product-unit">{service.unit || 'Service'}</p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '30px 20px', color: '#999', fontSize: '12px' }}>
                          No services found
                        </div>
                      )
                    )}
                  </div>
                </div>

              </div>
            </form>
          </div>
        </div>
      </div>

      {/* -- Add New Customer Modal ------------------------------------------- */}
      {showAddCustomerModal && (
        <div className="add-customer-modal-overlay" onClick={handleCloseAddCustomerModal}>
          <div className="add-customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="add-customer-modal-header">
              <div>
                <h2>Add New Customer</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.7 }}>
                  Enter customer details. If customer exists, they will be linked to your account.
                </p>
              </div>
              <button onClick={handleCloseAddCustomerModal} className="add-customer-close-btn">
                <X size={24} />
              </button>
            </div>

            <div className="add-customer-modal-content">
              <div className="add-customer-form-section">
                
                {/* -- Basic Information -- */}
                <div>
                  <h4>
                    <User size={16} />
                    Basic Information
                  </h4>
                  <div className="add-customer-form-grid">
                    <div className="add-customer-form-group">
                      <label>Customer Name *</label>
                      <input
                        type="text"
                        value={newCustomerData.name}
                        onChange={(e) => handleNewCustomerChange('name', e.target.value)}
                        placeholder="Enter customer name"
                        disabled={savingCustomer || checkingCustomer}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="add-customer-form-group">
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          value={newCustomerData.phone}
                          onChange={(e) => handleNewCustomerChange('phone', e.target.value)}
                          onBlur={handleEmailBlur}
                          placeholder="10-digit number"
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                      <div className="add-customer-form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={newCustomerData.email}
                          onChange={(e) => handleNewCustomerChange('email', e.target.value)}
                          onBlur={handleEmailBlur}
                          placeholder="customer@email.com"
                          disabled={savingCustomer || checkingCustomer}
                        />
                        {checkingCustomer && (
                          <div className="add-customer-checking-badge">
                            <div style={{ 
                              width: '10px', 
                              height: '10px', 
                              border: '2px solid #92400e', 
                              borderTop: '2px solid transparent', 
                              borderRadius: '50%',
                              animation: 'spin 0.8s linear infinite'
                            }} />
                            Checking...
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {existingCustomer && (
                      <div className={`add-customer-existing-badge ${isAlreadyLinked ? 'already-linked' : ''}`} style={isAlreadyLinked ? {
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        border: '1px solid #fbbf24'
                      } : {}}>
                        {isAlreadyLinked ? (
                          <>?? Customer "{existingCustomer.name}" is already linked. Please select from dropdown instead.</>
                        ) : (
                          <>? Existing customer found: {existingCustomer.name} - Details auto-filled</>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="add-customer-form-group">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          value={newCustomerData.date_of_birth}
                          onChange={(e) => handleNewCustomerChange('date_of_birth', e.target.value)}
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                      <div className="add-customer-form-group">
                        <label>Gender</label>
                        <select
                          value={newCustomerData.gender}
                          onChange={(e) => handleNewCustomerChange('gender', e.target.value)}
                          disabled={savingCustomer || checkingCustomer}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* -- Billing Address -- */}
                <div>
                  <h4 
                    onClick={() => toggleSection('billingModal')}
                    style={{ 
                      cursor: 'pointer', 
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FileText size={16} />
                    Billing Address
                    <ChevronDown 
                      size={16} 
                      style={{ 
                        transition: 'transform 0.2s',
                        transform: collapsedSections.billingModal ? 'rotate(-90deg)' : 'rotate(0deg)'
                      }}
                    />
                  </h4>
                  {!collapsedSections.billingModal && (
                    <div className="add-customer-form-grid">
                      <div className="add-customer-form-group">
                        <label>Address</label>
                        <textarea
                          value={newCustomerData.billing_address}
                          onChange={(e) => handleNewCustomerChange('billing_address', e.target.value)}
                          placeholder="Full billing address"
                          rows="2"
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="add-customer-form-group">
                          <label>Flat/Plot Number</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_flat_plot_number}
                            onChange={(e) => handleNewCustomerChange('billing_flat_plot_number', e.target.value)}
                            placeholder="C-210"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>Floor/Lane No.</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_floor_lane_no}
                            onChange={(e) => handleNewCustomerChange('billing_floor_lane_no', e.target.value)}
                            placeholder="2nd Floor"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="add-customer-form-group">
                          <label>Building/Colony Name</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_building_colony_name}
                            onChange={(e) => handleNewCustomerChange('billing_building_colony_name', e.target.value)}
                            placeholder="Lakeview Residency"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>Society/Area Name</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_society_area_name}
                            onChange={(e) => handleNewCustomerChange('billing_society_area_name', e.target.value)}
                            placeholder="Koramangala"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        <div className="add-customer-form-group">
                          <label>City</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_city}
                            onChange={(e) => handleNewCustomerChange('billing_city', e.target.value)}
                            placeholder="City"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>State</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_state}
                            onChange={(e) => handleNewCustomerChange('billing_state', e.target.value)}
                            placeholder="State"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>Pincode</label>
                          <input
                            type="text"
                            value={newCustomerData.billing_pincode}
                            onChange={(e) => handleNewCustomerChange('billing_pincode', e.target.value)}
                            placeholder="Pincode"
                            disabled={savingCustomer || checkingCustomer}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* -- Service Address -- */}
                <div>
                  <h4 
                    onClick={() => toggleSection('serviceModal')}
                    style={{ 
                      cursor: 'pointer', 
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <MapPin size={16} />
                    Service Address
                    <ChevronDown 
                      size={16} 
                      style={{ 
                        transition: 'transform 0.2s',
                        transform: collapsedSections.serviceModal ? 'rotate(-90deg)' : 'rotate(0deg)'
                      }}
                    />
                  </h4>
                  {!collapsedSections.serviceModal && (
                    <div className="add-customer-form-grid">
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '8px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '6px',
                        marginBottom: '12px'
                      }}>
                        <input
                          type="checkbox"
                          id="same-as-billing"
                          checked={newCustomerData.same_as_service}
                          onChange={(e) => handleSameAsBillingChange(e.target.checked)}
                          disabled={savingCustomer || checkingCustomer}
                          style={{ cursor: 'pointer' }}
                        />
                        <label 
                          htmlFor="same-as-billing" 
                          style={{ 
                            margin: 0, 
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          Same as Billing Address
                        </label>
                      </div>

                      <div className="add-customer-form-group">
                        <label>Address</label>
                        <textarea
                          value={newCustomerData.service_address}
                          onChange={(e) => handleNewCustomerChange('service_address', e.target.value)}
                          placeholder="Full service address"
                          rows="2"
                          disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="add-customer-form-group">
                          <label>Flat/Plot Number</label>
                          <input
                            type="text"
                            value={newCustomerData.service_flat_plot_number}
                            onChange={(e) => handleNewCustomerChange('service_flat_plot_number', e.target.value)}
                            placeholder="C-210"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>Floor/Lane No.</label>
                          <input
                            type="text"
                            value={newCustomerData.service_floor_lane_no}
                            onChange={(e) => handleNewCustomerChange('service_floor_lane_no', e.target.value)}
                            placeholder="2nd Floor"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="add-customer-form-group">
                          <label>Building/Colony Name</label>
                          <input
                            type="text"
                            value={newCustomerData.service_building_colony_name}
                            onChange={(e) => handleNewCustomerChange('service_building_colony_name', e.target.value)}
                            placeholder="Lakeview Residency"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>Society/Area Name</label>
                          <input
                            type="text"
                            value={newCustomerData.service_society_area_name}
                            onChange={(e) => handleNewCustomerChange('service_society_area_name', e.target.value)}
                            placeholder="Koramangala"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        <div className="add-customer-form-group">
                          <label>City</label>
                          <input
                            type="text"
                            value={newCustomerData.service_city}
                            onChange={(e) => handleNewCustomerChange('service_city', e.target.value)}
                            placeholder="City"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>State</label>
                          <input
                            type="text"
                            value={newCustomerData.service_state}
                            onChange={(e) => handleNewCustomerChange('service_state', e.target.value)}
                            placeholder="State"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                        <div className="add-customer-form-group">
                          <label>Pincode</label>
                          <input
                            type="text"
                            value={newCustomerData.service_pincode}
                            onChange={(e) => handleNewCustomerChange('service_pincode', e.target.value)}
                            placeholder="Pincode"
                            disabled={savingCustomer || checkingCustomer || newCustomerData.same_as_service}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* -- GST Information (Optional) -- */}
                <div>
                  <h4>
                    <FileText size={16} />
                    GST Information (Optional)
                  </h4>
                  <div className="add-customer-form-grid">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="add-customer-form-group">
                        <label>GSTIN</label>
                        <input
                          type="text"
                          value={newCustomerData.gstin}
                          onChange={(e) => handleNewCustomerChange('gstin', e.target.value)}
                          placeholder="GST Number"
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                      <div className="add-customer-form-group">
                        <label>PAN Number</label>
                        <input
                          type="text"
                          value={newCustomerData.pan_number}
                          onChange={(e) => handleNewCustomerChange('pan_number', e.target.value)}
                          placeholder="PAN Number"
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="add-customer-form-group">
                        <label>Legal Name</label>
                        <input
                          type="text"
                          value={newCustomerData.legal_name}
                          onChange={(e) => handleNewCustomerChange('legal_name', e.target.value)}
                          placeholder="Legal business name"
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                      <div className="add-customer-form-group">
                        <label>Trade Name</label>
                        <input
                          type="text"
                          value={newCustomerData.trade_name}
                          onChange={(e) => handleNewCustomerChange('trade_name', e.target.value)}
                          placeholder="Trade name"
                          disabled={savingCustomer || checkingCustomer}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="add-customer-form-group">
                        <label>GST State Code</label>
                        <input
                          type="text"
                          value={newCustomerData.gst_state_code}
                          onChange={(e) => handleNewCustomerChange('gst_state_code', e.target.value)}
                          placeholder="e.g., 29 for Karnataka"
                          disabled={savingCustomer || checkingCustomer}
                          maxLength="2"
                        />
                      </div>
                      <div className="add-customer-form-group">
                        <label>GST Registration Type</label>
                        <select
                          value={newCustomerData.gst_registration_type}
                          onChange={(e) => handleNewCustomerChange('gst_registration_type', e.target.value)}
                          disabled={savingCustomer || checkingCustomer}
                        >
                          <option value="regular">Regular</option>
                          <option value="composition">Composition</option>
                          <option value="unregistered">Unregistered</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* -- Action Buttons -- */}
                <div className="add-customer-form-actions">
                  <button
                    type="button"
                    onClick={handleCloseAddCustomerModal}
                    disabled={savingCustomer || checkingCustomer}
                    className="add-customer-btn-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveNewCustomer}
                    disabled={savingCustomer || checkingCustomer || isAlreadyLinked}
                    className="add-customer-btn-save"
                    style={isAlreadyLinked ? { 
                      opacity: 0.5, 
                      cursor: 'not-allowed',
                      backgroundColor: '#ccc' 
                    } : {}}
                  >
                    {savingCustomer ? (
                      <>
                        <div style={{ 
                          width: '14px', 
                          height: '14px', 
                          border: '2px solid white', 
                          borderTop: '2px solid transparent', 
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite'
                        }} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        {isAlreadyLinked 
                          ? 'Already Linked' 
                          : existingCustomer 
                            ? 'Link Existing Customer' 
                            : 'Save Customer'
                        }
                      </>
                    )}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuotationForm;
